<template>
  <div class="organization-positions-tab-wrapper">
    <!-- Grid / Categorized View (Table) — Categorized reuses the same list (MDP-615) -->
    <div v-if="currentView === 'Grid' || currentView === 'Categorized'" class="org-pos-tab">
      <OrganizationPositionsTable
        :positions="filteredPositions"
        :pagination="paginationData"
        :loading="isLoading"
        :initial-load-complete="hasInitialPositionsLoad"
        :searchText="searchText || viewState.state.filters.searchText"
        :appliedFilters="appliedFilters"
        :backendPagination="true"
        :title="title"
        :actionLabel="actionLabel"
        :hideTopSection="hideTopSection"
        :hideActiveNo="hideActiveNo"
        :activeRecordsCount="activeRecordsCount"
        @positionClick="handlePositionDrillDown"
        @updatePositionsViewSelection="handleViewSelectionChange"
      />
    </div>

    <!-- Pane View (Tree + Detail) -->
    <div v-else-if="currentView === 'Pane'" class="pane-view-container">
      <!-- Search Modal for Filters -->
      <FilterPositionsModal
        v-if="ideaMdp440Enabled"
        :value="showPaneFilterDialog"
        :initial-statuses="paneFilterModalInitialStatuses"
        :initial-include-archived="paneFilterModalInitialIncludeArchived"
        :initial-radio-filters="paneFilterModalInitialRadioFilters"
        :show-radio-groups="ideaMdp551Enabled"
        :initial-department="paneDepartmentFilter"
        :initial-scope="paneDepartmentScope"
        :departments="paneFilterModalDepartments"
        :loading="paneFilterModalDepartmentsLoading"
        @input="showPaneFilterDialog = $event"
        @close="showPaneFilterDialog = false"
        @search="handlePaneDepartmentSearch"
        @apply="handlePaneFilterModalApply"
      />
      <SearchModal
        v-else
        :dialog="showPaneFilterDialog"
        @back="showPaneFilterDialog = false"
        @checkedItemsChanged="handlePaneApplyFilters"
        :clearSelection="paneFilterClearSelection"
        filterType="Positions"
        :filterGroups="viewState.state.filters.filterGroups"
        :groupsItems="viewState.state.filters.groupsItems"
        :statusOptions="viewState.state.filters.statusOptions"
        :modalWidth="620"
        :defaultSelected="paneAppliedFilters"
      />

      <!-- Add Position Modal (tree view) -->
      <AddPositionDialog
        :show="showPaneAddPositionModal"
        OrgObjectType="Position"
        ghostText="+ Add"
        type="add"
        :buttonLabel="isPaneAddPositionSaving ? 'Save as Draft' : 'Save as Draft'"
        :allowSpecialCharacters="true"
        @onAdd="handlePaneAddPositionSave"
        @close="showPaneAddPositionModal = false"
      />

      <!-- Filters + view toggle: full width above the split (unchanged from original bar layout) -->
      <div class="pane-view-filters-bar">
        <div class="pane-view-filters-bar-inner">
          <FilterByDepartmentTag
            v-if="ideaMdp440Enabled && paneDepartmentFilter"
            :department-name="paneDepartmentFilter.name"
            :scope="paneDepartmentScope === 'direct' ? 'direct-only' : 'with-sub-depts'"
            class="pane-department-filter-tag"
            @toggle-scope="handlePaneToggleDepartmentScope"
            @clear="handlePaneClearDepartmentFilter"
          />
          <SearchBar
            inputBox
            :add="false"
            type="button"
            :label="paneFilterChips.length > 0 ? 'More Filters' : 'Filters'"
            :length="paneFilterChips.length"
            :chipText="paneFilterChips"
            ghostText="Search"
            @onClick="handlePaneSearchBarClick"
            @removeFilter="handlePaneRemoveFilter"
            class="pane-search-bar-filters-only"
            removeColumnClass
          />
          <MyButton
            class="ml-2 search-add-btn pane-filters-add-btn"
            label="Add Position"
            icon="plusicon.svg"
            normal="normal-hover"
            @onClick="() => handlePaneSearchBarClick('add')"
          />
          <div v-if="showOrganizationPositionsViewSelector" class="pane-view-selector-wrapper">
            <PositionsViewSelector
              :selection="'Pane'"
              :hasCategorizedView="hasCategorizedView"
              @update:selection="handleViewSelectionChange"
            />
          </div>
        </div>
      </div>

      <div ref="paneViewLayoutRef" class="pane-view-layout">
        <!-- Left: search only above tree (Add Position stays in top filters bar) -->
        <div
          class="pane-view-left"
          :style="{
            width: `${paneLeftWidthPercent}%`,
            ...(paneViewRightHeight != null ? { maxHeight: `${paneViewRightHeight}px` } : {}),
          }"
        >
          <div v-if="positionTreeDndAndSearchEnabled" class="pane-left-tree-search-row">
            <ExTextInput
              ghostText="Search"
              icon="interface_search"
              class="pane-tree-search-input filter-input-search"
              clearText="clear search"
              :value="paneSearchText"
              @input="handlePaneSearch"
              @onFocus="handlePaneSearchFocus"
              @onBlur="handlePaneSearchBlur"
            />
          </div>

          <div
            ref="paneLeftRef"
            :class="[
              'pane-view-left-scroll',
              {
                'pane-view-left-scroll--has-content': navPaneHasAny,
                'pane-view-left-scroll--empty': !navPaneHasAny,
              },
            ]"
            tabindex="-1"
            @scroll="handleTreeScroll"
            @click.capture="focusLeftScrollContainer"
          >
            <PositionsSearchAndNavigate
              ref="positionsSearchNavigateRef"
              :data="dataForPositionsNavigate"
              :flat-search-mode="flatSearchActive"
              :selected-item="selectedItem"
              :loading-more="flatSearchActive ? paneSearchLoading : treePositionsLoadingMore"
              :has-more="flatSearchActive ? false : treePositions.length < treePositionsTotal"
              :on-checkpoint-dropped-on-responsibility="
                positionTreeDndAndSearchEnabled ? handleCheckpointDroppedOnTree : undefined
              "
              :checkpoint-drag-source="
                positionTreeDndAndSearchEnabled ? checkpointDragSource : undefined
              "
              :on-responsibility-dropped-on-role="
                positionTreeDndAndSearchEnabled ? handleResponsibilityDroppedOnTree : undefined
              "
              :responsibility-drag-source="
                positionTreeDndAndSearchEnabled ? responsibilityDragSource : undefined
              "
              :on-role-dropped-on-position="
                positionTreeDndAndSearchEnabled ? handleRoleDroppedOnTree : undefined
              "
              :role-drag-source="positionTreeDndAndSearchEnabled ? roleDragSource : undefined"
              :position-attached-role-ids-by-position="panePositionAttachedRoleIds"
              :role-attached-responsibility-ids-by-role="paneRoleAttachedResponsibilityIds"
              @item-selected="handleTreeItemSelected"
              @toggle-expand="handleTreeToggleExpand"
              @checkpoint-drag-over="handleCheckpointDragOverState"
              @responsibility-role-drag-over="handleResponsibilityDragOverState"
              @role-position-drag-over="handleRoleDragOverState"
            />
          </div>
        </div>

        <!-- Resize handle: default 33%, user can drag down to 25% -->
        <div
          class="pane-view-resize-handle"
          role="separator"
          aria-label="Resize panels"
          @mousedown="handlePaneResizeStart"
        />

        <!-- Right: Detail View -->
        <div ref="paneViewRightRef" class="pane-view-right">
          <div v-if="navigatePaneEmpty" class="pane-view-empty">
            <div class="empty-state">
              <p>{{ flatSearchActive ? "No matches" : "No positions found" }}</p>
            </div>
          </div>
          <PositionForm
            v-else-if="selectedItem && selectedItem.type === 'position' && selectedItem.id"
            :positionId="selectedItem.id"
            :name="selectedItem.name"
            :key="`position-${selectedItem.id}`"
            :drilldownInPanel="currentView === 'Pane'"
            :allow-pane-tree-drag-drop="positionTreeDndAndSearchEnabled"
            :on-role-drag-start-callback="
              positionTreeDndAndSearchEnabled ? handleRoleDragStartFromTable : undefined
            "
            :on-role-drag-end-callback="
              positionTreeDndAndSearchEnabled ? handleRoleDragEndFromTable : undefined
            "
            @drilldown="handleDrilldown"
            @refresh-position-data="handlePaneTreeRefresh"
            @attached-roles-search-sync="handlePaneAttachedRolesSearchSync"
            @position-attached-roles-sync="handlePanePositionAttachedRolesSync"
          />
          <div v-else-if="paneNoPositionDetail" class="pane-view-empty pane-no-position-detail">
            <div class="empty-state">
              <p>No position found</p>
              <ExTextInput
                ghostText="Search Role"
                icon="interface_search"
                class="pane-tree-search-input filter-input-search mt-4"
                clearText="clear search"
                :value="paneAttachedRolesSearchBackup"
                @input="handlePaneAttachedRolesSearchSync"
              />
            </div>
          </div>
          <RoleCard
            v-else-if="selectedItem && selectedItem.type === 'role' && selectedItem.id"
            :roleId="selectedItem.id"
            :name="selectedItem.name"
            :key="`role-${selectedItem.id}-${roleAttachRefreshKey}`"
            :drilldownInPanel="currentView === 'Pane'"
            :breadcrumbsList="paneRoleBreadcrumbs"
            :allow-drag-responsibility-to-tree="
              currentView === 'Pane' && positionTreeDndAndSearchEnabled
            "
            :on-responsibility-drag-start-callback="
              positionTreeDndAndSearchEnabled ? handleResponsibilityDragStartFromTable : undefined
            "
            :on-responsibility-drag-end-callback="
              positionTreeDndAndSearchEnabled ? handleResponsibilityDragEndFromTable : undefined
            "
            @drilldown="handleDrilldown"
            @roleDataUpdated="handlePaneTreeRefresh"
            @role-attached-responsibilities-sync="handlePaneRoleAttachedResponsibilitiesSync"
            @pane-after-responsibility-attached="handlePaneAfterResponsibilityAttachedFromRoleTab"
            @item-deleted="handleItemDeleted"
          />
          <ResponsibilityCard
            v-else-if="selectedItem && selectedItem.type === 'responsibility' && selectedItem.id"
            :resId="selectedItem.id"
            :name="selectedItem.name"
            :key="`responsibility-${selectedItem.id}-${checkpointMoveRefreshKey}`"
            :allow-drag-checkpoint-to-tree="
              currentView === 'Pane' && positionTreeDndAndSearchEnabled
            "
            :drilldownInPanel="currentView === 'Pane'"
            :on-checkpoint-drag-start-callback="
              positionTreeDndAndSearchEnabled ? handleCheckpointDragStartFromTable : undefined
            "
            :on-checkpoint-drag-end-callback="
              positionTreeDndAndSearchEnabled ? handleCheckpointDragEndFromTable : undefined
            "
            @drilldown="handleDrilldown"
            @responsibilityDataUpdated="handlePaneTreeRefresh"
            @item-deleted="handleItemDeleted"
          />
          <EmptyState
            v-else-if="paneDeletedItem"
            icon="alert-circle"
            :title="
              paneDeletedItem.type === 'role'
                ? 'Role successfully deleted'
                : 'Responsibility successfully deleted'
            "
            supportingText="Select a new branch in the tree"
          />
          <div v-else class="pane-view-empty">
            <div class="empty-state">
              <p>Select an item from the tree to view details</p>
            </div>
          </div>
        </div>
      </div>
      <!-- During checkpoint drag: overlay hides native cursor; custom component is the only cursor -->
      <Teleport to="body">
        <template v-if="paneTreeExternalDragActive">
          <div class="pane-checkpoint-drag-cursor-overlay" aria-hidden="true" />
          <div
            class="pane-checkpoint-drag-cursor"
            :style="{
              left: dragCursorX - paneDragCursorLeftOffset + paneDragCursorLeftInsetPx + 'px',
              top: dragCursorY - 12 + paneDragCursorTopInsetPx + 'px',
            }"
          >
            <DragAndDropInPositionsTreeNavigatorCursor
              :action="paneTreeDragCursorAction"
              :state="paneTreeDragCursorUiState"
            />
          </div>
        </template>
      </Teleport>
      <DropToMoveCheckpointDialog
        :show="showDropToMoveDialog"
        :checkpoint-name="pendingDropMove?.checkpointName || ''"
        :responsibility-name="pendingDropMove?.targetResponsibilityName || ''"
        @onMove="handleConfirmDropToMove"
        @onClose="handleCloseDropToMoveDialog"
      />
      <DropToAttachResponsibilityToRoleDialog
        :value="showDropToAttachResponsibilityDialog"
        :responsibility-name="pendingResponsibilityAttach?.responsibilityLabel || ''"
        :role-name="pendingResponsibilityAttach?.targetRoleLabel || ''"
        @attach="handleConfirmDropToAttachResponsibility"
        @input="onDropToAttachResponsibilityDialogInput"
      />
      <DropToAttachRoleToPositionDialog
        :value="showDropToAttachRoleDialog"
        :role-name="pendingRoleAttach?.roleLabel || ''"
        :position-name="pendingRoleAttach?.targetPositionLabel || ''"
        @attach="handleConfirmDropToAttachRole"
        @input="onDropToAttachRoleDialogInput"
      />
    </div>
  </div>
</template>

<script setup>
import {
  onMounted,
  onBeforeUnmount,
  computed,
  getCurrentInstance,
  ref,
  watch,
  Teleport,
} from "vue";
import OrganizationPositionsTable from "@components/organizations/OrganizationPositionsTable/OrganizationPositionsTable.vue";
import PositionsSearchAndNavigate from "@components/PositionsSearchAndNavigate/PositionsSearchAndNavigate.vue";
import PositionsViewSelector from "@components/PositionsViewSelector/PositionsViewSelector.vue";
import PositionForm from "@components/PositionForm/PositionForm.vue";
import RoleCard from "@components/RoleCard/RoleCard.vue";
import ResponsibilityCard from "@components/ResponsibilityCard/ResponsibilityCard.vue";
import EmptyState from "@components/EmptyState/EmptyState.vue";
import SearchBar from "@components/SearchBar/SearchBar.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import FilterPositionsModal from "@components/FilterPositionsModal/FilterPositionsModal.vue";
import FilterByDepartmentTag from "@components/FilterByDepartmentTag/FilterByDepartmentTag.vue";
import { LD_FLAG_IDEA_MDP_440, LD_FLAG_IDEA_MDP_551 } from "@components/constants/launchDarklyFlagKeys";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import MyButton from "@components/buttons/Button.vue";
import DragAndDropInPositionsTreeNavigatorCursor from "@components/DragAndDropInPositionsTreeNavigatorCursor/DragAndDropInPositionsTreeNavigatorCursor.vue";
import DropToMoveCheckpointDialog from "@components/DropToMoveCheckpointDialog/DropToMoveCheckpointDialog.vue";
import DropToAttachResponsibilityToRoleDialog from "@components/RolesCatalog/DropToAttachResponsibilityToRoleDialog/DropToAttachResponsibilityToRoleDialog.vue";
import DropToAttachRoleToPositionDialog from "@components/RolesCatalog/DropToAttachRoleToPositionDialog/DropToAttachRoleToPositionDialog.vue";
import AddPositionDialog from "@components/AddPositionDialog/AddPositionDialog.vue";
import { usePositions } from "@/composables/positions/usePositions";
import { useHighlightRow } from "@/composables/common/useHighlightRow";
import {
  resolveOrganizationPositionStatusFilters,
  resolveMdp551PositionApiFilters,
  applyMdp551PositionFiltersToPositionsStore,
  useOrganizationPositionsViewState,
} from "@components/organizations/OrganizationPositionsTable/useViewState";
import {
  normalizeOrganizationPositionsViewSelection,
  ORGANIZATION_POSITIONS_LIST_VIEW_SELECTION,
  redirectOrganizationPositionsToListViewIfNeeded,
  shouldShowOrganizationPositionsViewSelector,
} from "@components/utils/preparingForOrganizationUi";
import { isProductReadinessEnabled } from "@/utils/accountProductFlags";
import {
  getListOfRolesAttachedToPosition,
  getListOfResponsibilitiesAttachedToRole,
  getDirectResponsibilitiesAttachedToPosition,
  getListOfPositionsV2,
  getListOfRolesV2,
  getListofResponsibilityV2,
  getListOfPositionsAttachedToRole,
  getResponsibilityIdV2,
  getPositionId,
  getRoleId,
  moveCheckpointOnResponsibility,
  attachResponsibilityToRole,
  attachRoleToPosition,
  getResponsibilityAttachments,
  getListOfDepartments,
} from "@/api";
import { mapApiDepartmentsToPickerList } from "@components/DepartmentPickerField/departmentPickerFieldUtils.js";
import { buildPositionRadioFilterPayload } from "@components/FilterPositionsModal/positionFilterPayload";
import { pickTreeActivationFields } from "@components/organizations/treeActivationFields";

const app = getCurrentInstance();
const { proxy } = app;

/**
 * Same pattern as `featureFlags/ideaMDP442`: value from Vuex (App.vue + `checkFeatureFlags` sync from LaunchDarkly).
 * If no store (e.g. isolated test), treat as off.
 */
const positionTreeDndAndSearchEnabled = computed(
  () =>
    isProductReadinessEnabled() &&
    proxy?.$store?.getters?.["featureFlags/dragDropAttachOrgObjectsPositionTreeMDP434"] ===
      true
);

/** MDP-440: new FilterPositionsModal + department filter; off → legacy SearchModal. */
const ideaMdp440Enabled = ref(false);
/** MDP-551 (ER-10494): radio filter groups (Validity / Occupied / Preparing for) in the new modal. */
const ideaMdp551Enabled = ref(false);

const emit = defineEmits(["drilldown"]);

const positions = usePositions();
const { highlight } = useHighlightRow();
const viewState = useOrganizationPositionsViewState();

const showOrganizationPositionsViewSelector = computed(() =>
  shouldShowOrganizationPositionsViewSelector()
);

/** MDP-615 — whether the Categorized segment is available (flag + at least one column). */
const hasCategorizedView = computed(
  () =>
    typeof viewState.hasCategorizedView === "function" &&
    viewState.hasCategorizedView()
);

// View state
const currentView = ref("Grid"); // 'Grid' or 'Pane'
const selectedItem = ref(null);
const positionsSearchNavigateRef = ref(null);
const paneViewRightRef = ref(null);
const paneViewRightHeight = ref(null);
const checkpointMoveRefreshKey = ref(0);
const roleAttachRefreshKey = ref(0);
const isCheckpointDragging = ref(false);
const checkpointDragSource = ref(null);
const checkpointDragCursorState = ref("Dragging");
const isResponsibilityToRoleDragging = ref(false);
const responsibilityDragSessionActive = ref(false);
const responsibilityDragSource = ref(null);
const responsibilityDragCursorState = ref("Dragging");
const dragCursorX = ref(0);
const dragCursorY = ref(0);
const showDropToMoveDialog = ref(false);
const pendingDropMove = ref(null);
const showDropToAttachResponsibilityDialog = ref(false);
const pendingResponsibilityAttach = ref(null);
const showDropToAttachRoleDialog = ref(false);
const pendingRoleAttach = ref(null);
const isRoleToPositionDragging = ref(false);
/** Role drag session (prefetch/tree); attach hand UI only when pointer leaves the right pane. */
const roleDragSessionActive = ref(false);
const roleDragSource = ref(null);
const roleDragCursorState = ref("Dragging");
const showPaneAddPositionModal = ref(false);
const isPaneAddPositionSaving = ref(false);
const paneLeftWidthPercent = ref(33); // Default 33%; user can drag down to 25%
const paneViewLayoutRef = ref(null);
let paneViewRightResizeObserver = null;
let dragEndCleanup = null;
let responsibilityDragEndCleanup = null;
let roleDragEndCleanup = null;
/** Invalidates in-flight role→position prefetch when drag ends or a new drag starts. */
let roleDragAttachmentPrefetchGen = 0;
let roleDragLeftBulkPrefetchStarted = false;
/** Invalidates in-flight responsibility→role prefetch when drag ends or a new drag starts. */
let responsibilityDragAttachmentPrefetchGen = 0;

// Pane view search and filter state
const paneSearchText = ref("");
/** Right pane: selected position was removed from tree (e.g. deleted); keep role search text. */
const paneNoPositionDetail = ref(false);
/** Right pane: a role/responsibility was deleted — show the shared EmptyState instead of navigating away. Holds { type: 'role' | 'responsibility' }. */
const paneDeletedItem = ref(null);
const paneAttachedRolesSearchBackup = ref("");
/** positionId -> role row ids attached to that position (tree expand + right panel sync for drag-drop validity). */
const panePositionAttachedRoleIds = ref({});
/** roleId -> responsibility ids (detail table + tree expand + post-attach) for resp→role drag duplicate detection */
const paneRoleAttachedResponsibilityIds = ref({});
/** After picking a row from flat search results, show full tree + selection while keeping the query in the input. */
const paneRevealTreeAfterFlatPick = ref(false);
const paneSearchFlatRows = ref([]);
const paneSearchLoading = ref(false);
const paneAppliedFilters = ref([]);
const showPaneFilterDialog = ref(false);
const paneFilterClearSelection = ref("");
const paneFilterModalDepartments = ref([]);
const paneFilterModalDepartmentsLoading = ref(false);
const paneDepartmentFilter = ref(null);
const paneDepartmentScope = ref("subtree");
const filteredTreeData = ref([]);
/** When set, `getListOfPositionsV2` includes `name` so the pane tree lists only matching positions (cleared on full rebuild / Grid). */
const paneTreePositionListNameScope = ref(null);

const STATUS_FILTER_LABELS = {
  draft: "Draft",
  active: "Active",
  retired: "Retired",
  inactive: "Inactive",
};

const paneFilterModalInitialStatuses = computed(() => ({
  draft: paneAppliedFilters.value.some((f) => String(f).toLowerCase() === "draft"),
  active: paneAppliedFilters.value.some((f) => String(f).toLowerCase() === "active"),
  retired: paneAppliedFilters.value.some((f) => String(f).toLowerCase() === "retired"),
  inactive: paneAppliedFilters.value.some((f) => String(f).toLowerCase() === "inactive"),
}));

const paneFilterModalInitialIncludeArchived = computed(
  () =>
    paneAppliedFilters.value.includes("Show") ||
    paneAppliedFilters.value.includes("show_archived")
);

// ER-10494 / MDP-551: current radio group selections (Validity / Occupied / Preparing for),
// rebuilt from the persisted label tokens each time the modal opens.
const paneFilterModalInitialRadioFilters = computed(() =>
  buildPositionRadioFilterPayload(paneAppliedFilters.value)
);

const paneFilterChips = computed(() =>
  paneAppliedFilters.value.filter((f) => f && f !== "Do not Show")
);

const props = defineProps({
  appliedFilters: {
    type: Array,
    default: () => [],
  },
  searchText: {
    type: String,
    default: "",
  },
  title: {
    type: String,
    default: "Positions Organization",
  },
  actionLabel: {
    type: String,
    default: "Add Position",
  },
  hideTopSection: {
    type: Boolean,
    default: true,
  },
  hideActiveNo: {
    type: Boolean,
    default: false,
  },
  activeRecordsCount: {
    type: Number,
    default: null,
  },
});

const paneTreeExternalDragActive = computed(
  () =>
    currentView.value === "Pane" &&
    (isCheckpointDragging.value ||
      (responsibilityDragSessionActive.value && isResponsibilityToRoleDragging.value) ||
      (roleDragSessionActive.value && isRoleToPositionDragging.value))
);

const paneTreeDragCursorAction = computed(() =>
  isResponsibilityToRoleDragging.value || isRoleToPositionDragging.value ? "Attach" : "Move"
);

const paneTreeDragCursorUiState = computed(() => {
  const state = isRoleToPositionDragging.value
    ? roleDragCursorState.value
    : isResponsibilityToRoleDragging.value
      ? responsibilityDragCursorState.value
      : checkpointDragCursorState.value;
  if (state === "Allowed") return "Allowed";
  if (state === "Blocked") return "Blocked";
  return "Dragging";
});

/** Align teleported hand+label with the ghost’s reserved column (see RoleResponsibilitiesTable / EditCheckpointsTable). */
const paneDragCursorLeftOffset = computed(() => {
  const rowPickOffsetX = 28;
  if (isResponsibilityToRoleDragging.value || isRoleToPositionDragging.value)
    return rowPickOffsetX + 72;
  return rowPickOffsetX + 32;
});

/** Nudge cursor right to sit inside the card: ghost border (2px) + inner padding (6px) matches RoleResponsibilitiesTable ghost. */
const paneDragCursorLeftInsetPx = computed(() =>
  isResponsibilityToRoleDragging.value || isRoleToPositionDragging.value ? 8 : 0
);

/** Match ghost padding-top so the hand stack aligns vertically with the card content. */
const paneDragCursorTopInsetPx = computed(() =>
  isResponsibilityToRoleDragging.value || isRoleToPositionDragging.value ? 6 : 0
);

const filteredPositions = computed(() => {
  return positions.allPositions.value || [];
});

const paginationData = computed(() => ({
  page: Math.floor(positions.paginationDefaultParams.value.offset / 10) + 1,
  total: positions.getPositionsCount(),
  perPage: 10,
}));

/** Matches `usePositions().loadAllPositions` → `$loader.show()` / `hide()` (Vuex `isLoading`, not `appLoading`). */
const isLoading = computed(() => proxy.$loader?.isLoading ?? false);
/** False until the first list fetch settles, so an empty-account state is never shown for "not loaded yet". */
const hasInitialPositionsLoad = ref(false);

// Measure right panel (pane-view-right) height and sync to left panel max-height
const logPaneViewRightHeight = () => {
  const el = paneViewRightRef.value;
  if (!el) return;
  const height = el.getBoundingClientRect().height;
  paneViewRightHeight.value = height;
};

// Observe right panel size and log height when it changes
const attachPaneViewRightHeightObserver = () => {
  const el = paneViewRightRef.value;
  if (!el || paneViewRightResizeObserver) return;
  logPaneViewRightHeight();
  paneViewRightResizeObserver = new ResizeObserver(() => {
    logPaneViewRightHeight();
  });
  paneViewRightResizeObserver.observe(el);
};

const detachPaneViewRightHeightObserver = () => {
  if (paneViewRightResizeObserver && paneViewRightRef.value) {
    paneViewRightResizeObserver.disconnect();
    paneViewRightResizeObserver = null;
  }
};

// Seed pane filters from shared status/archived state; department is pane-specific.
const ensurePaneDefaultFilters = () => {
  const shared = viewState.state.filters.values;
  const seed =
    Array.isArray(shared) && shared.length ? [...shared] : [...paneAppliedFilters.value];
  paneAppliedFilters.value = resolveOrganizationPositionStatusFilters(seed);
  paneDepartmentFilter.value = viewState.state.filters.paneDepartment || null;
  paneDepartmentScope.value = viewState.state.filters.paneDepartmentScope || "subtree";
};

// Write pane status filters to shared state; persist pane department separately from grid.
const syncPaneFiltersToViewState = () => {
  viewState.applyFilters([...paneAppliedFilters.value]);
  viewState.setDepartmentFilter(paneDepartmentFilter.value, paneDepartmentScope.value, "pane");
};

/** Pane → list: persist pane filters to shared state, sync positions store, reload list API. */
const activateGridViewFromPane = async (targetView = "Grid") => {
  currentView.value = targetView;
  paneRevealTreeAfterFlatPick.value = false;
  paneTreePositionListNameScope.value = null;
  paneNoPositionDetail.value = false;
  selectedItem.value = null;

  if (proxy?.$router) {
    proxy.$router
      .replace({ name: "NewOrganization", params: { tab: "positions" } })
      .catch(() => {});
  }

  syncPaneFiltersToViewState();
  viewState.pushFilterValuesToPositionsStore(viewState.state.filters.values);

  positions.setPaginationDataLimit(10);
  positions.setPaginationDataOffset(0);
  await positions.loadAllPositions();
};

const MIN_LEFT_PANE_PERCENT = 25;
const MAX_LEFT_PANE_PERCENT = 33;

function handlePaneResizeStart(e) {
  if (e.button !== 0) return;
  e.preventDefault();
  const layoutEl = paneViewLayoutRef.value;
  if (!layoutEl) return;
  const onMove = (moveEvent) => {
    const rect = layoutEl.getBoundingClientRect();
    const x = moveEvent.clientX - rect.left;
    const percent = Math.round((x / rect.width) * 100);
    paneLeftWidthPercent.value = Math.min(
      MAX_LEFT_PANE_PERCENT,
      Math.max(MIN_LEFT_PANE_PERCENT, percent)
    );
  };
  const onUp = () => {
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseup", onUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };
  document.body.style.cursor = "col-resize";
  document.body.style.userSelect = "none";
  document.addEventListener("mousemove", onMove);
  document.addEventListener("mouseup", onUp);
}

// Initialize currentView from viewState
const initializeView = () => {
  const savedSelection = normalizeOrganizationPositionsViewSelection(
    viewState.config.positionsViewSelection || ORGANIZATION_POSITIONS_LIST_VIEW_SELECTION
  );
  viewState.config.positionsViewSelection = savedSelection;

  if (!showOrganizationPositionsViewSelector.value) {
    currentView.value = ORGANIZATION_POSITIONS_LIST_VIEW_SELECTION;
    viewState.config.positionsViewSelection = ORGANIZATION_POSITIONS_LIST_VIEW_SELECTION;
    paneRevealTreeAfterFlatPick.value = false;
    paneTreePositionListNameScope.value = null;
    return;
  }

  if (savedSelection === "Pane") {
    currentView.value = "Pane";
    ensurePaneDefaultFilters();
    rebuildTreeDataWithFilters().then(() => {
      if (!selectedItem.value) {
        selectFirstPosition();
      }
    });
  } else if (savedSelection === "Categorized") {
    currentView.value = "Categorized";
    paneRevealTreeAfterFlatPick.value = false;
    paneTreePositionListNameScope.value = null;
  } else {
    currentView.value = ORGANIZATION_POSITIONS_LIST_VIEW_SELECTION;
    viewState.config.positionsViewSelection = ORGANIZATION_POSITIONS_LIST_VIEW_SELECTION;
    paneRevealTreeAfterFlatPick.value = false;
    paneTreePositionListNameScope.value = null;
  }
};

// When Pane view is shown, observe right panel height; when leaving Pane, disconnect
watch(
  () => currentView.value,
  (newVal) => {
    if (newVal === "Pane") {
      proxy.$nextTick(() => attachPaneViewRightHeightObserver());
    } else {
      detachPaneViewRightHeightObserver();
    }
  },
  { immediate: false }
);

// Watch for view selection changes from the table
watch(
  () => viewState.config.positionsViewSelection,
  (newSelection) => {
    if (!newSelection) return;

    if (!showOrganizationPositionsViewSelector.value) {
      if (viewState.config.positionsViewSelection !== ORGANIZATION_POSITIONS_LIST_VIEW_SELECTION) {
        viewState.config.positionsViewSelection = ORGANIZATION_POSITIONS_LIST_VIEW_SELECTION;
      }
      if (currentView.value !== ORGANIZATION_POSITIONS_LIST_VIEW_SELECTION) {
        void activateGridViewFromPane();
      }
      return;
    }

    if (newSelection === "Pane" && currentView.value !== "Pane") {
      currentView.value = "Pane";
      ensurePaneDefaultFilters();
      rebuildTreeDataWithFilters().then(() => {
        selectFirstPosition();
      });
    } else if (
      (newSelection === "Grid" || newSelection === "Categorized") &&
      currentView.value !== newSelection
    ) {
      if (currentView.value === "Pane") {
        void activateGridViewFromPane(newSelection);
      } else {
        currentView.value = newSelection;
      }
    }
  },
  { immediate: false }
);

const handleViewSelectionChange = (selection) => {
  const normalized = normalizeOrganizationPositionsViewSelection(selection);
  const hasCategorized =
    typeof viewState.hasCategorizedView === "function" &&
    viewState.hasCategorizedView();
  const effective = normalized === "Categorized" && !hasCategorized ? "Grid" : normalized;

  viewState.config.positionsViewSelection = effective;

  if (!showOrganizationPositionsViewSelector.value) {
    viewState.config.positionsViewSelection = ORGANIZATION_POSITIONS_LIST_VIEW_SELECTION;
    if (currentView.value !== ORGANIZATION_POSITIONS_LIST_VIEW_SELECTION) {
      void activateGridViewFromPane(ORGANIZATION_POSITIONS_LIST_VIEW_SELECTION);
    }
    return;
  }

  if (effective === "Pane" && currentView.value !== "Pane") {
    currentView.value = "Pane";
    ensurePaneDefaultFilters();
    rebuildTreeDataWithFilters().then(() => {
      selectFirstPosition();
    });
  } else if (
    (effective === "Grid" || effective === "Categorized") &&
    currentView.value !== effective
  ) {
    if (currentView.value === "Pane") {
      void activateGridViewFromPane(effective);
    } else {
      currentView.value = effective;
    }
  }
};

// Tree data for PositionsSearchAndNavigate
const treeData = ref([]);

// Pane tree: paginated positions (load 10 initially, more on scroll)
const TREE_PAGE_SIZE = 30;
const treePositions = ref([]);
const treePositionsTotal = ref(0);
const treePositionsLoadingMore = ref(false);
const paneLeftRef = ref(null);
let wheelListenerEl = null;
let paneFlatSearchDebounceTimer = null;

const paneSearchHasText = computed(() => paneSearchText.value.trim() !== "");

// Tree data for pane: filters via filterTreeData (search arg unused for filters-only path).
// When the user types in the pane search but is viewing the tree (after a pick or never used flat list),
// filter the tree by name/id so matches stay visible and selection can resolve to one role row.
const displayedTreeData = computed(() => {
  const base = treeData.value;
  const filters = paneAppliedFilters.value;
  const trimmed = positionTreeDndAndSearchEnabled.value ? paneSearchText.value.trim() : "";
  const hasText = trimmed !== "";
  const reveal = paneRevealTreeAfterFlatPick.value;
  // After a flat-search pick we keep the search text but switch the left pane to the full tree.
  // Client-side text filtering would prune parent branches before lazy-loaded role/responsibility
  // rows exist, so the selected row vanishes (esp. responsibilities whose title matches the query
  // but position/role names do not). Skip name filtering whenever we have a post-pick selection.
  const routeResId = proxy?.$route?.query?.resId;
  const selForFilter = selectedItem.value;
  const responsibilityMatchesRoute =
    routeResId != null &&
    routeResId !== "" &&
    selForFilter != null &&
    String(selForFilter.id) === String(routeResId) &&
    String(selForFilter.type || "").toLowerCase() === "responsibility";
  const selectedTypeLower = String(selForFilter?.type || "").toLowerCase();
  // While a responsibility is selected, never apply client-side tree text filtering (q).
  // Otherwise search "Al" keeps only positions matching "Al". A responsibility under e.g.
  // "Veronica…" lives on a branch that fails that filter — filterTreeData builds **cloned**
  // nodes (`{ ...position, children: filteredRoles }`), so lazy-loaded role/resp rows attach to
  // the real treeData nodes but the left pane still renders clones → no row matches selectedItem.
  const skipClientTextFilter =
    (reveal && hasText && selectedItem.value != null && selectedItem.value.id != null) ||
    (hasText && responsibilityMatchesRoute) ||
    (hasText && selectedTypeLower === "responsibility");
  const q = hasText && reveal && !skipClientTextFilter ? trimmed : "";
  if (filters.length === 0) {
    if (q) return filterTreeData(base, q, []);
    return base;
  }
  const afterFilters = filterTreeData(base, "", filters);
  if (q) return filterTreeData(afterFilters, q, []);
  return afterFilters;
});

/** Flat list mode: typed query exists and user has not yet chosen a row (then we show the full tree). */
const flatSearchActive = computed(
  () =>
    positionTreeDndAndSearchEnabled.value &&
    paneSearchHasText.value &&
    !paneRevealTreeAfterFlatPick.value
);

watch(positionTreeDndAndSearchEnabled, (enabled) => {
  if (enabled) return;
  if (currentView.value !== "Pane") return;
  paneSearchText.value = "";
  paneSearchFlatRows.value = [];
  paneRevealTreeAfterFlatPick.value = false;
  paneSearchLoading.value = false;
  paneTreePositionListNameScope.value = null;
});

/**
 * Left pane list data. When a responsibility is selected we MUST feed the canonical `treeData`
 * (same object references lazy expansion mutates). `displayedTreeData` can wrap filtered/cloned
 * branches that diverge from `treeData`, so highlight and expanded children disappear for QA.
 */
const dataForPositionsNavigate = computed(() => {
  if (flatSearchActive.value) {
    return paneSearchFlatRows.value;
  }
  const sel = selectedItem.value;
  if (
    sel != null &&
    String(sel.type || "").toLowerCase() === "responsibility" &&
    sel.id != null &&
    sel.id !== ""
  ) {
    return treeData.value;
  }
  return displayedTreeData.value;
});

/** Collect position node ids from the tree (or flat search rows) for role-attachment prefetch. */
function collectPaneNavigatePositionIds(items) {
  const out = [];
  const walk = (arr) => {
    for (const it of arr || []) {
      if (it?.type === "position" && it.id != null) {
        out.push(String(it.id));
      }
      if (it?.children?.length) walk(it.children);
    }
  };
  walk(items);
  return out;
}

/**
 * Load attached role ids for positions not yet present in panePositionAttachedRoleIds
 * so tree drag-over can block "already attached" without requiring every node to be expanded first.
 */
/**
 * Load role ids that already have this responsibility attached (API), merge into paneRoleAttachedResponsibilityIds
 * so tree drag-over shows blocked (red) before the target role row is expanded.
 */
async function prefetchRolesHavingResponsibilityAttachedForDrag(gen, responsibilityId) {
  if (currentView.value !== "Pane" || responsibilityId == null) return;
  const accountIdStr = localStorage.getItem("currentAccountID");
  const accountId = accountIdStr ? JSON.parse(accountIdStr) : null;
  if (!accountId) return;
  const rid = String(responsibilityId);
  try {
    const response = await getResponsibilityAttachments(accountId, rid);
    if (gen !== responsibilityDragAttachmentPrefetchGen) return;
    const roles = response?.data?.results || [];
    if (!roles.length) return;
    const next = { ...paneRoleAttachedResponsibilityIds.value };
    for (const role of roles) {
      const roleId = role?.id;
      if (roleId == null) continue;
      const key = String(roleId);
      const prev = next[key];
      const list = Array.isArray(prev) ? [...prev] : [];
      if (!list.some((id) => String(id) === rid)) list.push(rid);
      next[key] = list;
    }
    paneRoleAttachedResponsibilityIds.value = next;
  } catch {
    if (gen !== responsibilityDragAttachmentPrefetchGen) return;
  }
}

async function prefetchPanePositionAttachedRoleIdsForDrag(
  gen,
  { includeAllPositions = true } = {}
) {
  if (currentView.value !== "Pane") return;
  const accountIdStr = localStorage.getItem("currentAccountID");
  const accountId = accountIdStr ? JSON.parse(accountIdStr) : null;
  if (!accountId) return;

  const merged = { ...panePositionAttachedRoleIds.value };
  const fetchPositionRoleIds = async (positionId) => {
    try {
      const response = await getListOfRolesAttachedToPosition(accountId, 1000, 0, "", positionId, {
        archived: false,
      });
      const roles = response?.data?.results || [];
      return roles.map((r) => String(r.id));
    } catch {
      return [];
    }
  };

  const selectedPosId =
    selectedItem.value?.type === "position" && selectedItem.value?.id != null
      ? String(selectedItem.value.id)
      : null;
  if (selectedPosId && !(selectedPosId in merged)) {
    merged[selectedPosId] = await fetchPositionRoleIds(selectedPosId);
    if (gen !== roleDragAttachmentPrefetchGen) return;
    panePositionAttachedRoleIds.value = merged;
  }

  if (!includeAllPositions) return;

  const roots = dataForPositionsNavigate.value || [];
  const positionIds = [
    ...new Set(collectPaneNavigatePositionIds(roots).filter((id) => id != null && id !== "")),
  ];
  const toFetch = positionIds.filter((id) => !(id in merged));
  if (!toFetch.length) return;

  const results = await Promise.all(
    toFetch.map(async (positionId) => {
      const key = String(positionId);
      const ids = await fetchPositionRoleIds(positionId);
      return { key, ids };
    })
  );
  if (gen !== roleDragAttachmentPrefetchGen) return;
  for (const { key, ids } of results) {
    merged[key] = ids;
  }
  panePositionAttachedRoleIds.value = merged;
}

const navigatePaneEmpty = computed(() => {
  if (flatSearchActive.value) {
    return !paneSearchLoading.value && paneSearchFlatRows.value.length === 0;
  }
  if (selectedItem.value?.id != null) {
    return false;
  }
  return displayedTreeData.value.length === 0;
});

const navPaneHasAny = computed(() => {
  if (flatSearchActive.value) {
    return paneSearchFlatRows.value.length > 0 || paneSearchLoading.value;
  }
  return displayedTreeData.value.length > 0;
});

// Helper function to extract code number from name (e.g., "#4" or "#4 - Name" -> "4")
const extractCodeFromName = (name) => {
  if (!name) return "";
  // Match patterns like "#4", "#4 -", "#4 Name", etc.
  const codeMatch = name.match(/#(\d+)/);
  return codeMatch ? codeMatch[1] : "";
};

// Build display name without duplicating: API often returns code_str like "#123 - cdcc" and name "cdcc"
const buildDisplayName = (codeStr, name, fallbackLabel = "Item") => {
  const trimmedName = (name || "").trim();
  let code = "";

  if (codeStr) {
    // Extract code (e.g. "#123" from "#123 - Name"); collapse duplicate leading "#" from API
    const first = (codeStr.split(" ")[0] || "").trim();
    const m = first.match(/^#+(.+)$/);
    code = m ? `#${m[1]}` : first;
  }

  // If we couldn't get a code from codeStr, use fallback or just the name
  if (!code) return trimmedName || fallbackLabel;

  // If we have a code and a name, combine them
  return trimmedName ? `${code} - ${trimmedName}` : code;
};

// Helper function to check if item matches search (by name, id, or code)
const itemMatchesSearch = (item, searchLower) => {
  if (!item || !searchLower) return false;

  // Check name
  const nameMatch = item.name && item.name.toLowerCase().includes(searchLower);
  if (nameMatch) return true;

  // Check id
  const idMatch = item.id && item.id.toString().includes(searchLower);
  if (idMatch) return true;

  // Check code number
  const codeNumber = extractCodeFromName(item.name);
  if (codeNumber) {
    // Match exact code number or code with #
    const codeMatch = codeNumber.includes(searchLower) || `#${codeNumber}`.includes(searchLower);
    if (codeMatch) return true;
  }

  return false;
};

// Filter tree data based on search and filters
const filterTreeData = (data, searchText, filters) => {
  if (!data || data.length === 0) return [];

  const searchLower = searchText ? searchText.toLowerCase().trim() : "";

  // If no search text and no filters, return all data
  if (!searchLower && (!filters || filters.length === 0)) {
    return data;
  }

  // If there's a search text, filter the data
  if (searchLower) {
    const filtered = data
      .map((position) => {
        // Check if position matches search
        const positionMatches = itemMatchesSearch(position, searchLower);

        // If position matches, use the LIVE position node from treeData — not `{ ...position }`.
        // Lazy-loaded role/responsibility rows replace `*.children` on those same objects; shallow
        // clones keep a stale `children` reference, so the pane shows an “expanded” row with no
        // subtree and responsibility selection never renders.
        if (positionMatches) {
          return position;
        }

        // Position doesn't match, check roles
        let filteredRoles = [];
        if (position.children && position.children.length > 0) {
          filteredRoles = position.children
            .map((role) => {
              // Check if role matches search
              const roleMatches = itemMatchesSearch(role, searchLower);

              // Live role node so lazy-loaded responsibilities stay visible (same rationale as position).
              if (roleMatches) {
                return role;
              }

              // Role doesn't match, check responsibilities
              let filteredResponsibilities = [];
              if (role.children && role.children.length > 0) {
                filteredResponsibilities = role.children.filter((resp) => {
                  const respMatches = itemMatchesSearch(resp, searchLower);
                  return respMatches;
                });
              }

              // Include role only if it has matching responsibilities
              if (filteredResponsibilities.length > 0) {
                return {
                  ...role,
                  children: filteredResponsibilities,
                };
              }
              return null;
            })
            .filter(Boolean);
        }

        // Include position only if it has matching children (roles or responsibilities)
        if (filteredRoles.length > 0) {
          return {
            ...position,
            children: filteredRoles,
          };
        }
        return null;
      })
      .filter(Boolean);

    // Return filtered results (empty array if no matches)
    return filtered;
  }

  // If only filters are applied (no search), return all data
  // Filters are handled by rebuildTreeDataWithFilters which rebuilds the tree
  return data;
};

// Debounce function for search
const debounce = (func, delay) => {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
};

const handlePaneAttachedRolesSearchSync = (text) => {
  paneAttachedRolesSearchBackup.value = typeof text === "string" ? text : "";
};

const handlePanePositionAttachedRolesSync = (payload) => {
  const positionId = payload?.positionId;
  if (positionId == null || positionId === "") return;
  const roleIds = Array.isArray(payload?.roleIds) ? payload.roleIds.map((id) => String(id)) : [];
  panePositionAttachedRoleIds.value = {
    ...panePositionAttachedRoleIds.value,
    [String(positionId)]: roleIds,
  };
};

const handlePaneRoleAttachedResponsibilitiesSync = (payload) => {
  const roleId = payload?.roleId;
  if (roleId == null || roleId === "") return;
  const responsibilityIds = Array.isArray(payload?.responsibilityIds)
    ? payload.responsibilityIds.map((id) => String(id))
    : [];
  paneRoleAttachedResponsibilityIds.value = {
    ...paneRoleAttachedResponsibilityIds.value,
    [String(roleId)]: responsibilityIds,
  };
};

function mergeResponsibilityOntoRoleResponsibilityCache(targetRoleId, responsibilityId) {
  if (targetRoleId == null || responsibilityId == null) return;
  const key = String(targetRoleId);
  const rid = String(responsibilityId);
  const prev = paneRoleAttachedResponsibilityIds.value[key];
  const list = Array.isArray(prev) ? [...prev] : [];
  if (!list.some((id) => String(id) === rid)) list.push(rid);
  paneRoleAttachedResponsibilityIds.value = {
    ...paneRoleAttachedResponsibilityIds.value,
    [key]: list,
  };
}

/** Resolve a position id that actually contains the target role in the current tree (or via API). */
async function resolvePositionIdContainingTargetRole(targetRoleId, preferredPositionId) {
  const rid = String(targetRoleId);
  let positionId =
    preferredPositionId != null && preferredPositionId !== "" ? String(preferredPositionId) : null;
  if (positionId) {
    const node = findRoleUnderPosition(treeData.value, positionId, rid);
    if (!node) positionId = null;
  }
  if (!positionId) {
    const path = findPathToItem(treeData.value, rid, "role");
    positionId = path?.[0]?.id != null ? String(path[0].id) : null;
  }
  if (!positionId) {
    const accountIdStr = localStorage.getItem("currentAccountID");
    const accountIdParsed = accountIdStr ? JSON.parse(accountIdStr) : null;
    if (accountIdParsed) {
      const { positionId: fetched } = await fetchFirstPositionAttachedToRole(accountIdParsed, rid);
      if (fetched) positionId = String(fetched);
    }
  }
  return positionId || null;
}

/** After attaching a responsibility to a role, expand the branch and select the target role (RoleCard on the right). */
async function selectPaneTreeAfterResponsibilityAttachedToRole({
  targetRoleId,
  targetPositionId,
  responsibilityId,
  responsibilityName,
  responsibilityCodeStr,
  targetRoleName,
}) {
  if (targetRoleId == null) return;
  const roleIdStr = String(targetRoleId);
  const resIdStr = responsibilityId != null ? String(responsibilityId) : null;
  const positionId = await resolvePositionIdContainingTargetRole(roleIdStr, targetPositionId);
  if (!positionId) return;

  let roleNode = findRoleUnderPosition(treeData.value, positionId, roleIdStr);
  if (!roleNode) {
    await ensureRoleNodeUnderPosition(positionId, roleIdStr);
    await proxy.$nextTick();
    roleNode = findRoleUnderPosition(treeData.value, positionId, roleIdStr);
  }
  const respDisplayName =
    resIdStr != null
      ? responsibilityCodeStr || responsibilityName || `Responsibility #${resIdStr}`
      : "";

  await handleTreeToggleExpand({
    itemId: positionId,
    itemType: "position",
    isExpanded: true,
    skipRouteUpdate: true,
  });
  await proxy.$nextTick();
  if (positionsSearchNavigateRef.value?.expandNodes) {
    positionsSearchNavigateRef.value.expandNodes([String(positionId)]);
    await proxy.$nextTick();
  }

  await handleTreeToggleExpand({
    itemId: roleIdStr,
    itemType: "role",
    isExpanded: true,
    parentId: positionId,
    skipRouteUpdate: true,
  });
  await proxy.$nextTick();
  if (positionsSearchNavigateRef.value?.expandNodes) {
    positionsSearchNavigateRef.value.expandNodes([
      String(positionId),
      `${positionId}-${roleIdStr}`,
    ]);
    await proxy.$nextTick();
  }

  if (resIdStr != null && respDisplayName) {
    ensureResponsibilityNodeUnderRole(
      positionId,
      roleIdStr,
      { name: respDisplayName, id: resIdStr },
      resIdStr
    );
    await proxy.$nextTick();
  }

  const roleAfter = findRoleUnderPosition(treeData.value, positionId, roleIdStr);
  if (roleAfter) {
    await handleTreeItemSelected({
      id: roleAfter.id,
      type: roleAfter.type,
      name: roleAfter.name,
      data: roleAfter,
      parentPositionId: String(positionId),
      skipExpandPositionRoles: true,
    });
    return;
  }

  await applyTreeItemSelection(
    {
      id: roleIdStr,
      type: "role",
      name: targetRoleName || `Role #${roleIdStr}`,
      parentPositionId: String(positionId),
      data: { id: roleIdStr, name: targetRoleName || "" },
      skipExpandPositionRoles: true,
    },
    null
  );
}

/** Attach responsibility to role from Role tab (picker / attach-to-other-role): select target role in tree and show RoleCard. */
async function handlePaneAfterResponsibilityAttachedFromRoleTab(payload) {
  if (currentView.value !== "Pane") return;
  const { targetRoleId, responsibilityId, responsibilityName, responsibilityCodeStr } =
    payload || {};
  if (targetRoleId == null || responsibilityId == null) return;
  mergeResponsibilityOntoRoleResponsibilityCache(targetRoleId, responsibilityId);
  await handlePaneTreeRefresh();
  roleAttachRefreshKey.value += 1;
  await proxy.$nextTick();

  const rid = String(targetRoleId);
  let preferredPos = null;
  const sel = selectedItem.value;
  if (
    sel?.type === "role" &&
    String(sel.id) === rid &&
    sel.parentPositionId != null &&
    String(sel.parentPositionId) !== ""
  ) {
    preferredPos = String(sel.parentPositionId);
  }
  if (!preferredPos) {
    const rPos = proxy?.$route?.query?.positionId;
    if (rPos != null && String(rPos) !== "") {
      const rp = String(rPos);
      if (findRoleUnderPosition(treeData.value, rp, rid)) preferredPos = rp;
    }
  }

  await selectPaneTreeAfterResponsibilityAttachedToRole({
    targetRoleId,
    targetPositionId: preferredPos,
    responsibilityId,
    responsibilityName: responsibilityName || "",
    responsibilityCodeStr: responsibilityCodeStr || "",
    targetRoleName: payload?.targetRoleName || "",
  });
}

// Handle search in pane view
const handlePaneSearch = (searchText) => {
  paneRevealTreeAfterFlatPick.value = false;
  const previousSearch = paneSearchText.value;
  paneSearchText.value = searchText;
  if (!searchText?.trim()) {
    paneSearchFlatRows.value = [];
    paneSearchLoading.value = false;
  }
  // When user clears search, rebuild tree so we reload positions without name filter
  // and avoid persistent "No positions found" after a previous no-results search
  if (previousSearch && previousSearch.trim() !== "" && (!searchText || searchText.trim() === "")) {
    rebuildTreeDataWithFilters();
  }
};

const handlePaneSearchFocus = () => {
  if (currentView.value !== "Pane") return;
};

const handlePaneSearchBlur = () => {};

// Handle filter button click
// Search bar: Add Position opens modal; Filters open filter dialog
const handlePaneSearchBarClick = (ev) => {
  if (ev === "add") {
    showPaneAddPositionModal.value = true;
  } else {
    handlePaneFilterClick();
  }
};

const handlePaneFilterClick = () => {
  showPaneFilterDialog.value = true;
};

// Handle filter removal
const handlePaneRemoveFilter = async (removedFilter) => {
  paneRevealTreeAfterFlatPick.value = false;
  const index = paneAppliedFilters.value.indexOf(removedFilter);
  if (index > -1) {
    paneAppliedFilters.value.splice(index, 1);
  }
  syncPaneFiltersToViewState();
  // Rebuild tree data with updated filters
  await rebuildTreeDataWithFilters();
  // Re-attach wheel listener and ensure scroll container is valid after DOM update
  if (currentView.value === "Pane") {
    await proxy.$nextTick();
    removeLeftPanelWheelListener();
    attachLeftPanelWheelListener();
  }
};

// Handle applying filters from FilterPositionsModal
const handlePaneFilterModalApply = async (payload) => {
  paneRevealTreeAfterFlatPick.value = false;

  const newFilters = [];
  (payload?.status || []).forEach((key) => {
    if (STATUS_FILTER_LABELS[key]) {
      newFilters.push(STATUS_FILTER_LABELS[key]);
    }
  });
  if (payload?.includeArchived) {
    newFilters.push("Show");
  }
  // ER-10494 / MDP-551: single-select radio groups → human labels mapped to API params
  // by getPanePositionQueryParams / resolveMdp551PositionApiFilters.
  if (payload?.radioFilters) {
    Object.values(payload.radioFilters).forEach((value) => {
      if (value) newFilters.push(value);
    });
  }

  paneAppliedFilters.value = newFilters;
  paneDepartmentFilter.value = payload?.department || null;
  paneDepartmentScope.value = payload?.scope || "subtree";
  showPaneFilterDialog.value = false;
  syncPaneFiltersToViewState();

  await rebuildTreeDataWithFilters();
  if (currentView.value === "Pane") {
    await proxy.$nextTick();
    removeLeftPanelWheelListener();
    attachLeftPanelWheelListener();
  }
};

const handlePaneApplyFilters = async (filters) => {
  paneRevealTreeAfterFlatPick.value = false;
  const newFilters = filters
    .map((item) => {
      if (item.value !== null) {
        return typeof item === "string" ? item : item.value;
      }
      return null;
    })
    .filter(Boolean);

  paneAppliedFilters.value = newFilters;
  paneDepartmentFilter.value = null;
  paneDepartmentScope.value = "subtree";
  showPaneFilterDialog.value = false;
  syncPaneFiltersToViewState();

  await rebuildTreeDataWithFilters();
  if (currentView.value === "Pane") {
    await proxy.$nextTick();
    removeLeftPanelWheelListener();
    attachLeftPanelWheelListener();
  }
};

const handlePaneDepartmentSearch = async (query) => {
  paneFilterModalDepartmentsLoading.value = true;
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    if (!accountId) {
      paneFilterModalDepartments.value = [];
      return;
    }
    const { data } = await getListOfDepartments(
      accountId,
      query || "",
      { archived: false },
      { limit: 500, offset: 0 }
    );
    const results = Array.isArray(data) ? data : data?.results || [];
    paneFilterModalDepartments.value = mapApiDepartmentsToPickerList(results);
  } catch (error) {
    console.error("Error loading departments for position filter:", error);
    paneFilterModalDepartments.value = [];
  } finally {
    paneFilterModalDepartmentsLoading.value = false;
  }
};

const handlePaneToggleDepartmentScope = async () => {
  paneDepartmentScope.value = paneDepartmentScope.value === "direct" ? "subtree" : "direct";
  viewState.setDepartmentFilter(paneDepartmentFilter.value, paneDepartmentScope.value, "pane");
  await rebuildTreeDataWithFilters();
};

const handlePaneClearDepartmentFilter = async () => {
  paneDepartmentFilter.value = null;
  paneDepartmentScope.value = "subtree";
  viewState.setDepartmentFilter(null, "subtree", "pane");
  await rebuildTreeDataWithFilters();
};

// Build query params for pane tree positions (same filters as UI, for API)
const getPanePositionQueryParams = (limit, offset) => {
  const statusFilters = paneAppliedFilters.value.filter((f) =>
    ["draft", "active", "retired", "inactive"].includes(typeof f === "string" ? f.toLowerCase() : f)
  );
  const hasShowArchived =
    paneAppliedFilters.value.includes("Show") || paneAppliedFilters.value.includes("show_archived");
  const hasNoArchived =
    paneAppliedFilters.value.includes("Do not Show") ||
    paneAppliedFilters.value.includes("no_archived");
  const scopedName = paneTreePositionListNameScope.value?.trim?.();
  const params = {
    limit,
    offset,
    ordering: "name",
    status: statusFilters.length
      ? statusFilters.map((s) => (typeof s === "string" ? s.toLowerCase() : s))
      : undefined,
    archived: hasShowArchived ? true : hasNoArchived ? false : false,
    ...(scopedName ? { name: scopedName } : {}),
    // MDP-551 (ER-10494): Validity / Occupied / Preparing-for radio groups.
    ...resolveMdp551PositionApiFilters(paneAppliedFilters.value),
  };

  if (ideaMdp440Enabled.value && paneDepartmentFilter.value?.id != null) {
    params.department = paneDepartmentFilter.value.id;
    params.include_subdepartments = paneDepartmentScope.value !== "direct";
  }

  return params;
};

// Apply pane filters to the positions store (shared by rebuild and refresh)
const applyPaneFiltersToStore = () => {
  positions.clearPositionDynamicFilters();
  const statusFilters = paneAppliedFilters.value.filter((f) =>
    ["draft", "active", "retired", "inactive"].includes(typeof f === "string" ? f.toLowerCase() : f)
  );
  statusFilters.forEach((status) => {
    positions.addPositionFilter(
      "status",
      typeof status === "string" ? status.toLowerCase() : status
    );
  });
  const hasShowArchived =
    paneAppliedFilters.value.includes("Show") || paneAppliedFilters.value.includes("show_archived");
  const hasNoArchived =
    paneAppliedFilters.value.includes("Do not Show") ||
    paneAppliedFilters.value.includes("no_archived");
  if (hasShowArchived) positions.addPositionFilter("archived", true);
  else if (hasNoArchived) positions.addPositionFilter("archived", false);
  else positions.addPositionFilter("archived", false);

  // MDP-551 (ER-10494): Validity / Occupied / Preparing-for radio groups.
  applyMdp551PositionFiltersToPositionsStore(positions, paneAppliedFilters.value);

  if (ideaMdp440Enabled.value && paneDepartmentFilter.value?.id != null) {
    positions.addPositionFilter("department", paneDepartmentFilter.value.id);
    positions.addPositionFilter(
      "include_subdepartments",
      paneDepartmentScope.value !== "direct"
    );
  }
};

// Rebuild tree data with current filters (load first page only; more on scroll)
const rebuildTreeDataWithFilters = async () => {
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    if (!accountId) return;

    paneTreePositionListNameScope.value = null;

    applyPaneFiltersToStore();

    // Load first page of positions for tree (10 records)
    treePositions.value = [];
    treePositionsTotal.value = 0;
    proxy.$loader?.show();
    const queryParams = getPanePositionQueryParams(TREE_PAGE_SIZE, 0);
    const response = await getListOfPositionsV2({
      account_id: accountId,
      queryParamsObj: queryParams,
    });
    const results = response?.data?.results || [];
    const total = response?.data?.count ?? 0;
    treePositions.value = results;
    treePositionsTotal.value = total;
    proxy.$loader?.hide();

    await buildTreeData();
    if (!selectedItem.value) {
      selectFirstPosition();
    }
  } catch (error) {
    console.error("Error rebuilding tree data with filters:", error);
    proxy.$loader?.hide();
  }
};

// Refetch all currently loaded pages of positions (same filters) and rebuild tree. Used after attach/detach so left panel updates without losing selection or scroll.
const refreshTreeDataKeepingLoadedPages = async () => {
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    if (!accountId) return;

    const currentLength = treePositions.value.length;
    if (currentLength === 0) {
      await rebuildTreeDataWithFilters();
      return;
    }

    applyPaneFiltersToStore();

    const pageCount = Math.ceil(currentLength / TREE_PAGE_SIZE);
    const requests = [];
    for (let i = 0; i < pageCount; i++) {
      const offset = i * TREE_PAGE_SIZE;
      const queryParams = getPanePositionQueryParams(TREE_PAGE_SIZE, offset);
      requests.push(
        getListOfPositionsV2({
          account_id: accountId,
          queryParamsObj: queryParams,
        })
      );
    }
    const responses = await Promise.all(requests);
    const combined = [];
    let total = treePositionsTotal.value;
    for (const response of responses) {
      const results = response?.data?.results || [];
      combined.push(...results);
      if (response?.data?.count != null) total = response.data.count;
    }
    treePositions.value = combined;
    treePositionsTotal.value = total;
    await buildTreeData();
  } catch (error) {
    console.error("Error refreshing tree data keeping loaded pages:", error);
  }
};

// Load more positions when user scrolls to bottom (new API hit)
const loadMoreTreePositions = async () => {
  // With any pane search text, appending pages rebuilds the tree and collapses / desyncs selection.
  if (paneSearchHasText.value) return;
  if (treePositionsLoadingMore.value || treePositions.value.length >= treePositionsTotal.value)
    return;
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId) return;
  treePositionsLoadingMore.value = true;
  try {
    const offset = treePositions.value.length;
    const queryParams = getPanePositionQueryParams(TREE_PAGE_SIZE, offset);
    const response = await getListOfPositionsV2({
      account_id: accountId,
      queryParamsObj: queryParams,
    });
    const results = response?.data?.results || [];
    const total = response?.data?.count ?? treePositionsTotal.value;
    treePositions.value = [...treePositions.value, ...results];
    treePositionsTotal.value = total;
    await buildTreeData();
  } catch (error) {
    console.error("Error loading more tree positions:", error);
  } finally {
    treePositionsLoadingMore.value = false;
  }
};

// Focus left scroll container so wheel events scroll it after user clicks back on the list
const focusLeftScrollContainer = () => {
  paneLeftRef.value?.focus?.({ preventScroll: true });
};

// When user wheels over the left panel, scroll it (works even when focus is on right panel). Requires passive: false listener.
const handleTreeWheel = (e) => {
  const el = paneLeftRef.value;
  if (!el) return;
  const isOverLeftPanel = el.contains(e.target);
  if (isOverLeftPanel) {
    e.preventDefault();
    el.scrollTop = Math.max(
      0,
      Math.min(el.scrollTop + e.deltaY, el.scrollHeight - el.clientHeight)
    );
  }
};

// Scroll handler for left panel: load more when near bottom (always use ref so it works after selecting role/responsibility)
const handleTreeScroll = (e) => {
  if (flatSearchActive.value) return;
  // Same as loadMore: do not paginate while the search box has text (incl. after flat pick + tree view).
  if (paneSearchHasText.value) return;
  const el = paneLeftRef.value;
  if (!el || treePositionsLoadingMore.value) return;
  if (treePositions.value.length === 0) return; /* no positions: don't load more or show skeleton */
  if (treePositions.value.length >= treePositionsTotal.value) return;
  // Only react if scroll happened in our container (or a descendant)
  if (e?.target && !el.contains(e.target)) return;
  const { scrollTop, scrollHeight, clientHeight } = el;
  const threshold = 150;
  if (scrollHeight - (scrollTop + clientHeight) < threshold) {
    loadMoreTreePositions();
  }
};

// Attach non-passive wheel listener so preventDefault() works (browsers use passive: true by default)
function attachLeftPanelWheelListener() {
  if (wheelListenerEl) return;
  const el = paneLeftRef.value;
  if (!el) return;
  el.addEventListener("wheel", handleTreeWheel, { passive: false });
  wheelListenerEl = el;
}
function removeLeftPanelWheelListener() {
  if (!wheelListenerEl) return;
  wheelListenerEl.removeEventListener("wheel", handleTreeWheel);
  wheelListenerEl = null;
}

/** Whether a role row from getListOfRolesAttachedToPosition should show responsibility drill-down (API fields can differ per call). */
const roleRowHasAttachedResponsibilities = (role) => {
  if (!role) return false;
  if ((role.responsibility_attachment_count ?? 0) > 0) return true;
  if ((role.active_attached_responsibilities_count ?? 0) > 0) return true;
  if ((role.inactive_attached_responsibilities_count ?? 0) > 0) return true;
  if ((role.draft_responsibilities_count ?? 0) > 0) return true;
  return false;
};

/**
 * List/tree position rows: `roles_count` can be 0 while a draft (or uncounted) role is still attached.
 * Use role validity buckets + direct counts so the left tree shows expand/drill, not a leaf dot.
 */
const positionRowHasExpandableTreeChildren = (position) => {
  if (!position) return false;
  const rc = Number(position.roles_count) || 0;
  if (rc > 0) return true;
  const resp =
    Number(position.responsibilities_count ?? position.active_responsibilities_count) || 0;
  if (resp > 0) return true;
  const valid = Number(position.valid_roles_count) || 0;
  const invalid = Number(position.invalid_roles_count) || 0;
  if (valid + invalid > 0) return true;
  const directs = position.direct_responsibilities ?? position.directResponsibilities;
  if (Array.isArray(directs) && directs.length > 0) return true;
  return false;
};

function mergeRoleIdIntoPanePositionAttachedRoles(positionId, roleId) {
  if (positionId == null || positionId === "" || roleId == null || roleId === "") return;
  const p = String(positionId);
  const r = String(roleId);
  const prev = panePositionAttachedRoleIds.value[p] || [];
  if (prev.some((id) => String(id) === r)) return;
  panePositionAttachedRoleIds.value = {
    ...panePositionAttachedRoleIds.value,
    [p]: [...prev, r],
  };
}

/** Tree list: avatar only for primary occupant (not preparing-only assignees). */
const getPositionOccupantAvatarUrl = (position) => {
  const primary = position?.assigned_primary;
  if (!primary) return null;
  return primary.assigned_employee_avatar ?? null;
};

const getPositionOccupantDisplayName = (position) => {
  const primary = position?.assigned_primary;
  return primary?.assigned_employee_name ?? "";
};

const splitNameForAvatar = (fullName) => {
  const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: " ", lastName: " " };
  if (parts.length === 1) return { firstName: parts[0], lastName: parts[0] };
  return { firstName: parts[0], lastName: parts[parts.length - 1] };
};

const getPositionTreeOccupantFields = (position) => {
  if (position?.assigned_primary == null) {
    return { showOccupantAvatar: false };
  }
  const picture = getPositionOccupantAvatarUrl(position);
  const fullName = getPositionOccupantDisplayName(position);
  const { firstName, lastName } = splitNameForAvatar(fullName);
  return {
    showOccupantAvatar: true,
    occupantPicture: picture,
    occupantFirstName: firstName,
    occupantLastName: lastName,
    /** Primary assignee display name for positions tree tooltip */
    primaryOccupantName: fullName,
  };
};

const getSortCodeFromDisplayName = (displayName) => {
  const codeStr = extractCodeFromName(displayName || "");
  const num = parseInt(codeStr, 10);
  return Number.isFinite(num) ? num : 0;
};

/**
 * Responsibilities can attach to multiple roles. Resolution must be deterministic:
 * prefer explicit `role_id`, then `role_attachments` order from the API, then stable numeric id sort.
 * Random ordering caused intermittent QA failures (wrong position/branch expanded).
 */
const orderResponsibilityRoleCandidates = (raw, responsibilityRecord, candidates) => {
  const ids = (candidates || []).map((c) => String(c));
  if (ids.length <= 1) return ids;

  const candSet = new Set(ids);
  const ordered = [];
  const push = (rid) => {
    const s = rid != null ? String(rid) : "";
    if (!s || !candSet.has(s)) return;
    if (ordered.includes(s)) return;
    ordered.push(s);
  };

  if (raw?.role_id != null) push(raw.role_id);
  if (responsibilityRecord?.role_id != null) push(responsibilityRecord.role_id);

  const walkAttachments = (rec) => {
    if (!rec || typeof rec !== "object") return;
    const arr = rec.role_attachments;
    if (!Array.isArray(arr)) return;
    for (const ra of arr) {
      if (ra?.role_id != null) push(ra.role_id);
    }
  };
  walkAttachments(raw);
  walkAttachments(responsibilityRecord);

  const remaining = [...candSet].filter((id) => !ordered.includes(id));
  remaining.sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  return [...ordered, ...remaining];
};

const treeNodeTypeMatches = (a, b) =>
  String(a || "").toLowerCase() === String(b || "").toLowerCase();

/** Direct-attachment GET may return `{ results }`, a bare array, or `{ data: [] }`. */
const normalizeDirectAttachmentResults = (response) => {
  const data = response?.data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && Array.isArray(data.data)) return data.data;
  return [];
};

const mapRoleToPositionTreeChild = (role) => {
  const roleCodeStr =
    role.code_str ||
    (role.code != null && role.code !== ""
      ? `#${String(role.code).replace(/^#+/, "")}`
      : "");
  const roleDisplayName = buildDisplayName(
    roleCodeStr,
    role.name,
    `Role #${role.code || role.id}`
  );
  const responsibilityAttachmentCount = role.responsibility_attachment_count ?? 0;
  return {
    id: role.id.toString(),
    type: "role",
    name: roleDisplayName,
    expanded: false,
    children: [],
    hasLoadedChildren: false,
    responsibility_attachment_count: responsibilityAttachmentCount,
    hasResponsibilities: roleRowHasAttachedResponsibilities(role),
    ...pickTreeActivationFields(role),
  };
};

const mapDirectResponsibilityToPositionTreeChild = (resp) => {
  const respCodeStr =
    resp.code_str ||
    (resp.code != null && resp.code !== ""
      ? `#${String(resp.code).replace(/^#+/, "")}`
      : "");
  const respDisplayName = buildDisplayName(
    respCodeStr,
    resp.name,
    `Responsibility #${resp.code || resp.id}`
  );
  return {
    id: String(resp.id),
    type: "responsibility",
    name: respDisplayName,
    children: [],
    attachmentId: resp.attachment_id,
    ...pickTreeActivationFields(resp),
  };
};

const roleIdsFromPositionTreeChildren = (children) =>
  (children || [])
    .filter((child) => treeNodeTypeMatches(child?.type, "role"))
    .map((child) => String(child.id));

/** Positions list API may return `results`, a bare array, or `data[]` (see AttachedToPositionsTab). */
const normalizePositionListResults = (res) => {
  const raw = res?.data;
  if (Array.isArray(raw?.results)) return raw.results;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  return [];
};

/**
 * Resolve a parent position for a role (flat search selection). Retries help when the first page is empty
 * due to filters, id coercion, or alternate API response shapes.
 */
const fetchFirstPositionAttachedToRole = async (accountId, roleId) => {
  const ridStr = String(roleId);
  const ridNum = /^\d+$/.test(ridStr) ? Number(ridStr) : ridStr;
  const attempts = [
    () => getListOfPositionsAttachedToRole(accountId, ridStr, 80, 0, "", {}),
    () => getListOfPositionsAttachedToRole(accountId, ridNum, 80, 0, "", {}),
    () =>
      getListOfPositionsAttachedToRole(accountId, ridStr, 80, 0, "", {
        archived: true,
      }),
    () =>
      getListOfPositionsAttachedToRole(accountId, ridStr, 80, 0, "", {
        archived: false,
      }),
  ];
  for (const run of attempts) {
    try {
      const res = await run();
      const results = normalizePositionListResults(res);
      const pos = results[0];
      if (pos?.id != null) {
        return { positionId: String(pos.id), positionRaw: pos };
      }
    } catch (_) {
      /* try next */
    }
  }
  return { positionId: null, positionRaw: null };
};

const findRoleUnderPosition = (tree, positionId, roleId) => {
  const pos = tree.find(
    (n) => treeNodeTypeMatches(n.type, "position") && String(n.id) === String(positionId)
  );
  if (!pos?.children?.length) return null;
  return pos.children.find(
    (c) => treeNodeTypeMatches(c.type, "role") && String(c.id) === String(roleId)
  );
};

/**
 * Lazy role APIs can omit responsibilities that are linked only via attached_roles_data
 * (e.g. attached_to_role false). Without a tree node, selection highlight and route resId fail.
 */
const ensureResponsibilityNodeUnderRole = (parentPosId, parentRoleId, itemData, numericId) => {
  const roleNode = findRoleUnderPosition(treeData.value, String(parentPosId), String(parentRoleId));
  if (!roleNode) return;
  if (!Array.isArray(roleNode.children)) roleNode.children = [];
  const sid = String(numericId);
  if (
    roleNode.children.some(
      (c) => String(c.id) === sid && treeNodeTypeMatches(c.type, "responsibility")
    )
  ) {
    return;
  }
  const raw = itemData.raw ?? itemData.data?.raw ?? itemData.data;
  const nameFallback =
    raw?.name != null && raw?.code_str != null
      ? buildDisplayName(raw.code_str, raw.name, `Responsibility #${sid}`)
      : null;
  const activationSource =
    raw && typeof raw === "object"
      ? raw
      : itemData?.data && typeof itemData.data === "object"
        ? itemData.data
        : null;
  roleNode.children.push({
    id: sid,
    type: "responsibility",
    name: itemData.name || nameFallback || `Responsibility #${sid}`,
    children: [],
    ...pickTreeActivationFields(activationSource),
  });
};

/**
 * Lazy position→roles API can omit a role that still appears on responsibility.role_attachments.
 * Without a role row, expansion and selection highlight never attach under the position.
 */
const ensureRoleNodeUnderPosition = async (parentPosId, parentRoleId) => {
  const posId = String(parentPosId);
  const rid = String(parentRoleId);
  if (findRoleUnderPosition(treeData.value, posId, rid)) return true;

  const positionNode = findInTree(treeData.value, posId, "position");
  if (!positionNode) return false;

  const accountIdStr = localStorage.getItem("currentAccountID");
  const accountId = accountIdStr ? JSON.parse(accountIdStr) : null;
  if (!accountId) return false;

  try {
    const response = await getRoleId(accountId, rid);
    const role = response?.data;
    if (!role?.id) return false;

    if (!Array.isArray(positionNode.children)) positionNode.children = [];
    if (
      positionNode.children.some((c) => String(c.id) === rid && treeNodeTypeMatches(c.type, "role"))
    ) {
      return true;
    }

    positionNode.children.push(mapRoleToPositionTreeChild(role));
    positionNode.hasLoadedChildren = true;
    panePositionAttachedRoleIds.value = {
      ...panePositionAttachedRoleIds.value,
      [posId]: roleIdsFromPositionTreeChildren(positionNode.children),
    };
    return true;
  } catch (e) {
    console.error("ensureRoleNodeUnderPosition:", e);
    return false;
  }
};

/** All role ids from list/detail payloads (field names vary; responsibilities can attach to multiple roles). */
const collectRoleIdsFromResponsibilityRecord = (record) => {
  if (!record || typeof record !== "object") return [];
  const coerce = (r) => {
    if (r == null) return null;
    if (typeof r === "number" || typeof r === "string") return String(r);
    if (r.id != null) return String(r.id);
    return null;
  };
  const seen = new Set();
  const add = (r) => {
    const id = coerce(r);
    if (id) seen.add(id);
  };
  for (const key of ["attached_roles", "attached_roles_data", "attached_roles_list", "roles"]) {
    const arr = record[key];
    if (Array.isArray(arr)) arr.forEach(add);
  }
  if (Array.isArray(record.role_attachments)) {
    for (const ra of record.role_attachments) {
      if (ra?.role_id != null) add(ra.role_id);
    }
  }
  if (record.role_id != null) add(record.role_id);
  if (record.role) add(record.role);
  if (record.parent_role) add(record.parent_role);
  return [...seen];
};

/** Role row for URL/display when a responsibility has multiple attachments — match the role actually selected in the tree. */
const findAttachedRoleMetaForRoleId = (record, roleIdStr) => {
  if (!record || typeof record !== "object" || !roleIdStr) return null;
  const rid = String(roleIdStr);
  const lists = [
    record.attached_roles_data,
    record.attached_roles,
    record.attached_roles_list,
    record.roles,
  ];
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    const hit = list.find((x) => String(x?.id ?? x?.role_id) === rid);
    if (hit) return hit;
  }
  if (Array.isArray(record.role_attachments)) {
    return record.role_attachments.find((x) => String(x?.role_id ?? x?.id) === rid) || null;
  }
  return null;
};

const resolveAncestorsForFlatSelection = async (itemType, id, raw) => {
  const accountIdStr = localStorage.getItem("currentAccountID");
  const accountId = accountIdStr ? JSON.parse(accountIdStr) : null;
  if (!accountId) return null;
  const sid = String(id);

  if (itemType === "position") {
    return { positionId: sid, positionRaw: raw };
  }

  if (itemType === "role") {
    try {
      const { positionId, positionRaw } = await fetchFirstPositionAttachedToRole(accountId, sid);
      if (positionId != null && positionRaw?.id != null) {
        return {
          positionId,
          roleId: sid,
          positionRaw,
        };
      }
    } catch (e) {
      console.error("resolveAncestorsForFlatSelection role:", e);
    }
    return { positionId: null, roleId: sid };
  }

  if (itemType === "responsibility") {
    let responsibilityRecord = null;
    let candidates = collectRoleIdsFromResponsibilityRecord(raw);
    if (candidates.length === 0) {
      try {
        const detail = await getResponsibilityIdV2(accountId, sid, "responsibility");
        responsibilityRecord = detail?.data ?? null;
        candidates = collectRoleIdsFromResponsibilityRecord(responsibilityRecord);
      } catch (e) {
        console.error("resolveAncestorsForFlatSelection responsibility:", e);
      }
    }
    if (candidates.length === 0) {
      return {
        positionId: null,
        roleId: null,
        responsibilityId: sid,
        responsibilityRecord,
      };
    }

    const tryOrder = orderResponsibilityRoleCandidates(raw, responsibilityRecord, candidates);
    let roleId = null;
    let positionId = null;
    let positionRaw = null;
    for (const rid of tryOrder) {
      try {
        const got = await fetchFirstPositionAttachedToRole(accountId, rid);
        if (got.positionId != null) {
          roleId = rid;
          positionId = got.positionId;
          positionRaw = got.positionRaw;
          break;
        }
      } catch (_) {
        /* try next role */
      }
    }
    if (roleId == null) {
      roleId = candidates[0];
      try {
        const got = await fetchFirstPositionAttachedToRole(accountId, roleId);
        positionId = got.positionId;
        positionRaw = got.positionRaw;
      } catch (e) {
        console.error("resolveAncestorsForFlatSelection responsibility:", e);
        return {
          positionId: null,
          roleId,
          responsibilityId: sid,
          responsibilityRecord,
        };
      }
    }

    return {
      positionId,
      roleId,
      responsibilityId: sid,
      positionRaw,
      responsibilityRecord,
    };
  }

  return null;
};

const runPaneFlatSearch = async (rawQuery) => {
  if (!positionTreeDndAndSearchEnabled.value) {
    paneSearchFlatRows.value = [];
    return;
  }
  const q = rawQuery.trim();
  if (!q) {
    paneSearchFlatRows.value = [];
    return;
  }
  const accountIdStr = localStorage.getItem("currentAccountID");
  const accountId = accountIdStr ? JSON.parse(accountIdStr) : null;
  if (!accountId) return;

  const statusFilters = paneAppliedFilters.value.filter((f) =>
    ["draft", "active", "retired", "inactive"].includes(typeof f === "string" ? f.toLowerCase() : f)
  );
  const statusParams = statusFilters.length
    ? statusFilters.map((s) => (typeof s === "string" ? s.toLowerCase() : s))
    : undefined;
  const hasShowArchived =
    paneAppliedFilters.value.includes("Show") || paneAppliedFilters.value.includes("show_archived");
  const hasNoArchived =
    paneAppliedFilters.value.includes("Do not Show") ||
    paneAppliedFilters.value.includes("no_archived");
  const archived = hasShowArchived ? true : hasNoArchived ? false : false;

  const roleFilters = {
    ...(statusParams?.length ? { status: statusParams } : {}),
    archived,
  };
  const respFilters = {
    ...(statusParams?.length ? { status: statusParams } : {}),
    archived,
  };

  const searchLower = q.toLowerCase();
  paneSearchLoading.value = true;
  try {
    const [posRes, roleRes, respRes] = await Promise.all([
      getListOfPositionsV2({
        account_id: accountId,
        queryParamsObj: {
          limit: 500,
          offset: 0,
          ordering: "name",
          name: q,
          status: statusParams,
          archived,
        },
      }),
      getListOfRolesV2(accountId, 500, 0, q, roleFilters),
      getListofResponsibilityV2(accountId, 500, 0, q, respFilters),
    ]);

    const posRows = (posRes?.data?.results || [])
      .map((position) => {
        const posId = position.id?.toString?.() ?? position.id;
        const codeStr = position.code_str || (position.code ? `#${position.code}` : "");
        const displayName = buildDisplayName(
          codeStr,
          position.name,
          `Position #${position.code || position.id}`
        );
        if (!itemMatchesSearch({ name: displayName, id: posId }, searchLower)) {
          return null;
        }
        const rolesCount = position.roles_count ?? 0;
        const responsibilitiesCount =
          position.active_responsibilities_count ?? position.responsibilities_count ?? 0;
        const hasRolesOrResponsibilities = positionRowHasExpandableTreeChildren(position);
        return {
          id: posId,
          type: "position",
          name: displayName,
          expanded: false,
          children: [],
          hasLoadedChildren: false,
          roles_count: rolesCount,
          responsibilities_count: responsibilitiesCount,
          hasRolesOrResponsibilities,
          hasResponsibilities: false,
          directResponsibilities: Array.isArray(position.direct_responsibilities)
            ? position.direct_responsibilities
            : [],
          ...getPositionTreeOccupantFields(position),
          ...pickTreeActivationFields(position),
          raw: position,
          _sortCode: getSortCodeFromDisplayName(displayName),
        };
      })
      .filter(Boolean);

    const roleRows = (roleRes?.data?.results || [])
      .map((role) => {
        const roleCodeStr = role.code_str || (role.code ? `#${role.code}` : "");
        const roleDisplayName = buildDisplayName(
          roleCodeStr,
          role.name,
          `Role #${role.code || role.id}`
        );
        if (!itemMatchesSearch({ name: roleDisplayName, id: role.id }, searchLower)) {
          return null;
        }
        return {
          id: role.id.toString(),
          type: "role",
          name: roleDisplayName,
          expanded: false,
          children: [],
          hasLoadedChildren: false,
          responsibility_attachment_count: role.responsibility_attachment_count ?? 0,
          hasResponsibilities: roleRowHasAttachedResponsibilities(role),
          hasRolesOrResponsibilities: false,
          ...pickTreeActivationFields(role),
          _sortCode: getSortCodeFromDisplayName(roleDisplayName),
          raw: role,
        };
      })
      .filter(Boolean);

    const responsibilityHasAttachedRole = (resp) => {
      const n = Number(resp.attached_role_count ?? resp.count_role_attachments ?? 0);
      if (n > 0) return true;
      return Array.isArray(resp.attached_roles_data) && resp.attached_roles_data.length > 0;
    };

    const respRows = (respRes?.data?.results || [])
      .map((resp) => {
        if (!responsibilityHasAttachedRole(resp)) {
          return null;
        }
        const respCodeStr = resp.code_str || (resp.code ? `#${resp.code}` : "");
        const respDisplayName = buildDisplayName(
          respCodeStr,
          resp.name,
          `Responsibility #${resp.code || resp.id}`
        );
        if (!itemMatchesSearch({ name: respDisplayName, id: resp.id }, searchLower)) {
          return null;
        }
        return {
          id: resp.id.toString(),
          type: "responsibility",
          name: respDisplayName,
          children: [],
          hasRolesOrResponsibilities: false,
          hasResponsibilities: false,
          ...pickTreeActivationFields(resp),
          _sortCode: getSortCodeFromDisplayName(respDisplayName),
          raw: resp,
        };
      })
      .filter(Boolean);

    const merged = [...posRows, ...roleRows, ...respRows];
    merged.sort((a, b) => (b._sortCode || 0) - (a._sortCode || 0));
    paneSearchFlatRows.value = merged.map(({ _sortCode: _c, ...rest }) => rest);
  } catch (e) {
    console.error("runPaneFlatSearch:", e);
    paneSearchFlatRows.value = [];
  } finally {
    paneSearchLoading.value = false;
  }
};

watch(
  () => [paneSearchText.value, paneAppliedFilters.value],
  () => {
    if (currentView.value !== "Pane") return;
    clearTimeout(paneFlatSearchDebounceTimer);
    const t = paneSearchText.value?.trim() ?? "";
    if (!t) {
      paneSearchFlatRows.value = [];
      paneSearchLoading.value = false;
      return;
    }
    paneFlatSearchDebounceTimer = setTimeout(() => {
      runPaneFlatSearch(t);
    }, 250);
  },
  { deep: true }
);

// Build tree data from tree positions (pane paginated list; lazy load roles/responsibilities on expand)
const buildTreeData = async () => {
  try {
    const allPositions = treePositions.value || [];
    const nextPositionIdSet = new Set(
      allPositions.map((p) => String(p?.id?.toString?.() ?? p?.id ?? ""))
    );
    const prevMap = panePositionAttachedRoleIds.value || {};
    const pruned = {};
    for (const k of Object.keys(prevMap)) {
      if (nextPositionIdSet.has(String(k))) pruned[k] = prevMap[k];
    }
    panePositionAttachedRoleIds.value = pruned;

    // Only show loader for initial load (empty tree), not for append
    if (allPositions.length === 0) proxy.$loader?.show();

    // Transform positions data to tree format without pre-fetching roles/responsibilities
    const transformedData = [];
    const seenPositionIds = new Set();

    for (const position of allPositions) {
      const posId = position.id?.toString?.() ?? position.id;
      if (seenPositionIds.has(posId)) continue;
      seenPositionIds.add(posId);

      const codeStr = position.code_str || (position.code ? `#${position.code}` : "");
      const displayName = buildDisplayName(
        codeStr,
        position.name,
        `Position #${position.code || position.id}`
      );

      const rolesCount = position.roles_count ?? 0;
      const responsibilitiesCount =
        position.responsibilities_count ?? position.active_responsibilities_count ?? 0;
      const hasRolesOrResponsibilities = positionRowHasExpandableTreeChildren(position);

      transformedData.push({
        id: posId,
        type: "position",
        name: displayName,
        expanded: false,
        children: [],
        hasLoadedChildren: false,
        roles_count: rolesCount,
        responsibilities_count: responsibilitiesCount,
        hasRolesOrResponsibilities,
        directResponsibilities: Array.isArray(position.direct_responsibilities)
          ? position.direct_responsibilities
          : [],
        ...getPositionTreeOccupantFields(position),
        ...pickTreeActivationFields(position),
      });
    }

    treeData.value = transformedData;
    proxy.$loader?.hide();
  } catch (error) {
    console.error("Error building tree data:", error);
    treeData.value = [];
    proxy.$loader?.hide();
  }
};

// Handle tree item expansion to load data on demand
// When skipRouteUpdate is true (e.g. responsibility drill-down), do not overwrite route so resId/resTab stay
// parentId: position id when expanding a role — required so the same role id under different positions resolves to the correct node
const handleTreeToggleExpand = async ({
  itemId,
  itemType,
  isExpanded,
  skipRouteUpdate = false,
  parentId = null,
}) => {
  if (!isExpanded) return;

  const findAndLoadChildren = async (items, parentPositionIdForRole = null) => {
    for (const item of items) {
      let nodeMatches =
        String(item.id) === String(itemId) && treeNodeTypeMatches(item.type, itemType);
      if (
        nodeMatches &&
        itemType === "role" &&
        parentId != null &&
        String(parentId) !== String(parentPositionIdForRole)
      ) {
        nodeMatches = false;
      }
      if (nodeMatches) {
        if (!item.hasLoadedChildren) {
          try {
            const accountIdStr = localStorage.getItem("currentAccountID");
            const accountId = accountIdStr ? JSON.parse(accountIdStr) : null;
            if (!accountId) {
              console.error("No account ID found");
              return true;
            }

            if (itemType === "position") {
              // Load direct responsibilities and roles as siblings under the position
              const [rolesResponse, directResponse] = await Promise.all([
                getListOfRolesAttachedToPosition(
                  accountId,
                  1000,
                  0,
                  "",
                  itemId,
                  { archived: false }
                ),
                getDirectResponsibilitiesAttachedToPosition(accountId, itemId).catch((error) => {
                  console.error(
                    `Error loading direct responsibilities for position ${itemId}:`,
                    error
                  );
                  return { data: { results: item.directResponsibilities || [] } };
                }),
              ]);
              const roles = rolesResponse?.data?.results || [];
              const directs = normalizeDirectAttachmentResults(directResponse);
              const roleChildren = roles.map(mapRoleToPositionTreeChild);
              const directChildren = directs.map(mapDirectResponsibilityToPositionTreeChild);

              item.children = [...directChildren, ...roleChildren];
              item.hasLoadedChildren = true;
              panePositionAttachedRoleIds.value = {
                ...panePositionAttachedRoleIds.value,
                [String(itemId)]: roleIdsFromPositionTreeChildren(item.children),
              };
            } else if (itemType === "role") {
              // Load responsibilities for role
              const response = await getListOfResponsibilitiesAttachedToRole(
                accountId,
                1000,
                0,
                "",
                itemId,
                { archived: false }
              );
              const responsibilities = response?.data?.results || [];

              item.children = responsibilities.map((resp) => {
                const respCodeStr = resp.code_str || (resp.code ? `#${resp.code}` : "");
                const respDisplayName = buildDisplayName(
                  respCodeStr,
                  resp.name,
                  `Responsibility #${resp.code || resp.id}`
                );
                return {
                  id: resp.id.toString(),
                  type: "responsibility",
                  name: respDisplayName,
                  children: [],
                  ...pickTreeActivationFields(resp),
                };
              });
              item.hasLoadedChildren = true;
              paneRoleAttachedResponsibilityIds.value = {
                ...paneRoleAttachedResponsibilityIds.value,
                [String(itemId)]: item.children.map((c) => String(c.id)),
              };
            }
          } catch (error) {
            console.error(`Error loading children for ${itemType} ${itemId}:`, error);
          }
        }
        return true;
      }
      if (item.children && item.children.length > 0) {
        const nextParentPosId = item.type === "position" ? item.id : parentPositionIdForRole;
        if (await findAndLoadChildren(item.children, nextParentPosId)) return true;
      }
    }
    return false;
  };

  await findAndLoadChildren(treeData.value, null);

  // Update route params when expanding (same as when selecting) - skip if caller is drilling to responsibility so resId/resTab are not wiped
  if (isExpanded && !skipRouteUpdate) {
    updateRouteForTreeItem(itemId, itemType);
  }
};

// Select the first visible item in the left pane (respects pane search + flat list mode).
// When the user has a search query, never fall back to the full tree — that selected an unrelated position after delete.
const selectFirstPosition = () => {
  proxy.$nextTick(() => {
    const route = proxy?.$route;
    const rq = route?.query;
    // URL is source of truth for Pane drill-down: expand role/responsibility in the tree instead of selecting the first visible position row.
    if (
      currentView.value === "Pane" &&
      rq?.resId != null &&
      rq.resId !== "" &&
      rq?.roleId != null &&
      rq.roleId !== "" &&
      rq?.positionId != null &&
      rq.positionId !== ""
    ) {
      const rid = String(rq.resId);
      const sel = selectedItem.value;
      const already =
        sel != null &&
        String(sel.type || "").toLowerCase() === "responsibility" &&
        String(sel.id) === rid;
      if (!already) {
        void applyTreeItemSelection(
          {
            id: rid,
            type: "responsibility",
            name: sel?.name || `Responsibility #${rid}`,
            parentPositionId: String(rq.positionId),
            parentRoleId: String(rq.roleId),
            data: { raw: {} },
          },
          null
        );
      }
      return;
    }

    const keepResponsibilitySelection = () => {
      const sel = selectedItem.value;
      const t = String(sel?.type || "").toLowerCase();
      if (t !== "responsibility" || sel?.id == null) return false;
      const routeRes = route?.query?.resId;
      if (routeRes != null && routeRes !== "" && String(sel.id) === String(routeRes)) {
        return true;
      }
      // router.replace(query) can lag behind selectedItem when revealing the tree after a flat-search pick.
      if (
        paneRevealTreeAfterFlatPick.value &&
        sel.parentPositionId != null &&
        sel.parentPositionId !== "" &&
        sel.parentRoleId != null &&
        sel.parentRoleId !== ""
      ) {
        return true;
      }
      return false;
    };

    if (flatSearchActive.value) {
      const rows = paneSearchFlatRows.value || [];
      const first = rows[0];
      if (first) {
        handleTreeItemSelected({
          id: first.id,
          type: first.type,
          name: first.name,
          data: first,
          skipExpandPositionRoles: true,
          panePositionTab: "attached-roles",
        });
      } else if (!keepResponsibilitySelection()) {
        selectedItem.value = null;
      }
      return;
    }

    const hasPaneSearch = paneSearchText.value.trim() !== "";
    const dataToUse = hasPaneSearch
      ? displayedTreeData.value
      : displayedTreeData.value.length > 0
        ? displayedTreeData.value
        : treeData.value;

    if (dataToUse && dataToUse.length > 0) {
      if (keepResponsibilitySelection()) {
        return;
      }
      const firstPosition = dataToUse[0];
      if (firstPosition && firstPosition.type === "position") {
        handleTreeItemSelected({
          id: firstPosition.id,
          type: firstPosition.type,
          name: firstPosition.name,
          data: firstPosition,
          skipExpandPositionRoles: true,
          panePositionTab: "attached-roles",
        });
      }
    } else if (!keepResponsibilitySelection()) {
      selectedItem.value = null;
    }
  });
};

// Watch for changes in displayedTreeData and clear selection only when there are
// truly no positions (not when the pane search filter prunes lazy-loaded branches).
watch(
  () => displayedTreeData.value,
  (newData) => {
    if (currentView.value === "Pane" && (!newData || newData.length === 0)) {
      if (treeData.value && treeData.value.length > 0) return;
      selectedItem.value = null;
    }
  },
  { deep: true }
);

// Left panel wheel: attach non-passive listener when Pane view is shown so scroll works after using right panel
watch(
  currentView,
  (view) => {
    if (view === "Pane") {
      proxy.$nextTick().then(attachLeftPanelWheelListener);
    } else {
      removeLeftPanelWheelListener();
    }
  },
  { immediate: true }
);

// Find path of ancestor nodes (position, then role) to the given item for restore after refresh
const findPathToItem = (items, itemId, itemType, path = []) => {
  for (const item of items) {
    if (String(item.id) === String(itemId) && treeNodeTypeMatches(item.type, itemType)) return path;
    if (item.children && item.children.length > 0) {
      const found = findPathToItem(item.children, itemId, itemType, [
        ...path,
        { id: item.id, type: item.type },
      ]);
      if (found) return found;
    }
  }
  return null;
};

// Update route query params when drilling down in Pane view: positionId, roleId, resId (stay on Pane, no navigation away)
const updateRouteForTreeItem = (itemId, itemType) => {
  if (currentView.value !== "Pane") return;
  const router = proxy?.$router;
  const route = proxy?.$route;
  if (!router || !route) return;
  const path = findPathToItem(treeData.value, itemId, itemType);
  const query = { ...route.query };
  if (itemType === "position") {
    query.positionId = String(itemId);
    delete query.roleId;
    delete query.resId;
    delete query.resTab;
    const pt = selectedItem.value?.panePositionTab;
    if (pt === "purpose" || pt === "attached-roles" || pt === "assignments") {
      query.positionTab = pt;
    } else {
      query.positionTab = "attached-roles";
    }
  } else if (itemType === "role" && path && path.length >= 1) {
    query.positionId = String(path[0].id);
    query.roleId = String(itemId);
    delete query.resId;
    delete query.resTab;
    delete query.positionTab;
    // Default tab for role detail: Attached Responsibilities (avoid stale roleTab from URL)
    query.roleTab = "attached_responsibilities";
  } else if (itemType === "responsibility") {
    let positionIdForQuery = null;
    let roleIdForQuery = null;
    if (path && path.length >= 2) {
      positionIdForQuery = String(path[0].id);
      roleIdForQuery = String(path[1].id);
    } else {
      const sel = selectedItem.value;
      if (
        sel &&
        String(sel.id) === String(itemId) &&
        treeNodeTypeMatches(sel.type, "responsibility") &&
        sel.parentPositionId != null &&
        sel.parentPositionId !== "" &&
        sel.parentRoleId != null &&
        sel.parentRoleId !== ""
      ) {
        positionIdForQuery = String(sel.parentPositionId);
        roleIdForQuery = String(sel.parentRoleId);
      }
    }
    if (!positionIdForQuery || !roleIdForQuery) return;

    query.positionId = positionIdForQuery;
    query.roleId = roleIdForQuery;
    query.resId = String(itemId);
    delete query.positionTab;
    delete query.roleTab;
    if (!query.resTab) query.resTab = "checklist";

    const posNode = findInTree(treeData.value, positionIdForQuery, "position");
    const roleNode = findRoleUnderPosition(treeData.value, positionIdForQuery, roleIdForQuery);
    if (posNode?.name) query.positionName = posNode.name;
    if (roleNode?.name) {
      const sep = " - ";
      const idx = roleNode.name.indexOf(sep);
      if (idx !== -1) {
        query.roleCode = roleNode.name.slice(0, idx).trim();
        query.roleName = roleNode.name.slice(idx + sep.length).trim();
      } else {
        query.roleName = roleNode.name;
      }
    }
    const sel = selectedItem.value;
    if (
      sel &&
      String(sel.id) === String(itemId) &&
      treeNodeTypeMatches(sel.type, "responsibility")
    ) {
      const raw = sel.data?.raw ?? sel.data;
      const ar =
        findAttachedRoleMetaForRoleId(raw, roleIdForQuery) || raw?.attached_roles_data?.[0];
      if (ar && !query.roleName) {
        query.roleName = ar.name || "";
        if (ar.code_str) {
          const cidx = ar.code_str.indexOf(" - ");
          query.roleCode = cidx !== -1 ? ar.code_str.slice(0, cidx).trim() : ar.code_str.trim();
        }
      }
    }
  } else {
    return;
  }
  router.replace({ name: route.name, params: route.params, query }).catch(() => {});
};

// Find item in tree by id and type
const findInTree = (items, id, type) => {
  for (const item of items) {
    if (String(item.id) === String(id) && treeNodeTypeMatches(item.type, type)) return item;
    if (item.children && item.children.length > 0) {
      const found = findInTree(item.children, id, type);
      if (found) return found;
    }
  }
  return null;
};

// Breadcrumbs for RoleCard in Pane view: Position -> Role (clicking Position drills back to position)
const paneRoleBreadcrumbs = computed(() => {
  if (
    currentView.value !== "Pane" ||
    !selectedItem.value?.id ||
    selectedItem.value?.type !== "role"
  )
    return [];
  const path = findPathToItem(treeData.value, selectedItem.value.id, "role");
  if (!path || path.length < 1) return [];
  const position = path[0];
  return [
    { text: position.name || "Position", disabled: false, positionId: String(position.id) },
    { text: "Role", disabled: true },
  ];
});

/** Drop position/role/responsibility drill-down query keys (Pane) when the current tree selection is cleared. */
function clearPaneTreeSelectionRouteQuery() {
  const router = proxy?.$router;
  const route = proxy?.$route;
  if (!router || !route?.query) return;
  if (route.query.positionId == null && route.query.roleId == null && route.query.resId == null) {
    return;
  }
  const query = { ...route.query };
  delete query.positionId;
  delete query.roleId;
  delete query.resId;
  delete query.positionTab;
  delete query.roleTab;
  delete query.resTab;
  router.replace({ name: route.name, params: route.params, query }).catch(() => {});
}

/**
 * Selected position existed before refresh but is gone after (e.g. deleted): clear URL, refresh flat search
 * so the left list is not stale, then select first remaining row or show empty state.
 */
async function handlePaneSelectedPositionRemovedFromTree() {
  clearPaneTreeSelectionRouteQuery();

  selectedItem.value = null;

  const q = paneSearchText.value?.trim() ?? "";
  if (q) {
    await runPaneFlatSearch(q);
  } else {
    paneSearchFlatRows.value = [];
    paneSearchLoading.value = false;
  }

  const stillHasPositions = Array.isArray(treeData.value) && treeData.value.length > 0;
  if (stillHasPositions) {
    paneNoPositionDetail.value = false;
    await proxy.$nextTick();
    selectFirstPosition();
  } else {
    paneNoPositionDetail.value = true;
  }
}

/**
 * ER-11676: a role or responsibility was deleted from the pane detail view. Keep the user in-place,
 * clear the tree selection (no auto-select of a replacement) and render the shared EmptyState.
 */
async function handleItemDeleted(payload) {
  clearPaneTreeSelectionRouteQuery();
  selectedItem.value = null;
  paneNoPositionDetail.value = false;
  paneDeletedItem.value = { type: payload?.type || "role" };
  await handlePaneTreeRefresh();
}

// Clear the "item deleted" empty state as soon as the user selects another tree row.
watch(selectedItem, (item) => {
  if (item && item.id != null) {
    paneDeletedItem.value = null;
  }
});

// Refresh left tree when positions/roles/responsibilities are updated in detail panel (e.g. attach/detach role or responsibility)
const handlePaneTreeRefresh = async () => {
  if (currentView.value !== "Pane") return;

  const currentSelected = selectedItem.value;
  // Get path to selected item BEFORE rebuild (roles/responsibilities only exist when expanded)
  let path = currentSelected
    ? findPathToItem(treeData.value, currentSelected.id, currentSelected.type)
    : null;
  if (
    !path &&
    currentSelected?.type === "responsibility" &&
    currentSelected.parentPositionId != null &&
    currentSelected.parentPositionId !== ""
  ) {
    if (currentSelected.parentRoleId != null && currentSelected.parentRoleId !== "") {
      path = [
        { id: currentSelected.parentPositionId, type: "position" },
        { id: currentSelected.parentRoleId, type: "role" },
      ];
    } else {
      path = [{ id: currentSelected.parentPositionId, type: "position" }];
    }
  }

  // Refetch all currently loaded positions (same filters) so tree updates without losing selection or scroll
  await refreshTreeDataKeepingLoadedPages();
  await proxy.$nextTick();

  if (!currentSelected) return;

  if (
    treeNodeTypeMatches(currentSelected.type, "position") &&
    !findInTree(treeData.value, currentSelected.id, "position")
  ) {
    await handlePaneSelectedPositionRemovedFromTree();
    return;
  }

  // Re-load children for each ancestor so the selected node exists in tree again (lazy load)
  const idsToExpand = [];
  if (path && path.length > 0) {
    for (const node of path) {
      await handleTreeToggleExpand({
        itemId: node.id,
        itemType: node.type,
        isExpanded: true,
        parentId: node.type === "role" && path[0]?.type === "position" ? path[0].id : null,
      });
      if (node.type === "role" && path[0]?.type === "position") {
        idsToExpand.push(`${path[0].id}-${node.id}`);
      } else {
        idsToExpand.push(String(node.id));
      }
    }
    await proxy.$nextTick();
  }
  // Selected role: path is only [position] — expand the role under that position (same role id can exist under multiple positions)
  if (currentSelected.type === "role" && path?.[0]) {
    const pathHasRole = path.some((p) => p.type === "role");
    if (!pathHasRole) {
      await handleTreeToggleExpand({
        itemId: currentSelected.id,
        itemType: "role",
        isExpanded: true,
        parentId: path[0].id,
      });
      await proxy.$nextTick();
      idsToExpand.push(`${path[0].id}-${currentSelected.id}`);
    }
  }
  // When selected item is a position, load its roles so they show under the drill-down (e.g. after attaching a role)
  if (currentSelected.type === "position") {
    await handleTreeToggleExpand({
      itemId: currentSelected.id,
      itemType: "position",
      isExpanded: true,
    });
    idsToExpand.push(String(currentSelected.id));
    await proxy.$nextTick();
  }
  if (
    idsToExpand.length > 0 &&
    positionsSearchNavigateRef.value &&
    typeof positionsSearchNavigateRef.value.expandNodes === "function"
  ) {
    positionsSearchNavigateRef.value.expandNodes(idsToExpand);
    await proxy.$nextTick();
  }

  const updatedItem = findInTree(treeData.value, currentSelected.id, currentSelected.type);
  if (updatedItem) {
    const payload = {
      id: updatedItem.id,
      type: updatedItem.type,
      name: updatedItem.name,
      data: updatedItem,
    };
    const route = proxy?.$route;
    if (currentSelected.type === "responsibility") {
      payload.parentPositionId = currentSelected.parentPositionId ?? route?.query?.positionId;
      payload.parentRoleId = currentSelected.parentRoleId ?? route?.query?.roleId;
    } else if (currentSelected.type === "role") {
      payload.parentPositionId = currentSelected.parentPositionId ?? route?.query?.positionId;
    }
    handleTreeItemSelected(payload);
  } else if (currentSelected.type === "responsibility") {
    const routeForParents = proxy?.$route;
    const pp = currentSelected.parentPositionId ?? routeForParents?.query?.positionId;
    const pr = currentSelected.parentRoleId ?? routeForParents?.query?.roleId;
    if (pp != null && pp !== "" && pr != null && pr !== "") {
      await applyTreeItemSelection(
        {
          id: currentSelected.id,
          type: "responsibility",
          name: currentSelected.name || "",
          data: currentSelected.data,
          parentPositionId: String(pp),
          parentRoleId: String(pr),
        },
        null
      );
    }
  } else {
    // Selected item no longer in tree (e.g. role removed) – clear pane search so the list resets, then re-select
    paneNoPositionDetail.value = false;
    selectedItem.value = null;
    const router = proxy?.$router;
    const route = proxy?.$route;
    if (
      router &&
      route?.query &&
      (route.query.positionId != null || route.query.roleId != null || route.query.resId != null)
    ) {
      const query = { ...route.query };
      delete query.positionId;
      delete query.roleId;
      delete query.resId;
      delete query.positionTab;
      delete query.roleTab;
      delete query.resTab;
      router.replace({ name: route.name, params: route.params, query }).catch(() => {});
    }
    if (paneFlatSearchDebounceTimer) {
      clearTimeout(paneFlatSearchDebounceTimer);
      paneFlatSearchDebounceTimer = null;
    }
    paneSearchText.value = "";
    paneSearchFlatRows.value = [];
    paneSearchLoading.value = false;
    paneRevealTreeAfterFlatPick.value = false;
    selectFirstPosition();
  }
};

// Create position from tree view: save, then show new position in tree and select it
const handlePaneAddPositionSave = async (payload) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId) return;
  const name = typeof payload === "string" ? payload : (payload?.name ?? "");
  if (!name || !name.trim()) return;

  isPaneAddPositionSaving.value = true;
  proxy.$loader?.show();
  try {
    const newPosition = await positions.createPosition({ accountId, name });
    showPaneAddPositionModal.value = false;

    if (newPosition?.id != null) {
      // Prepend new position so it appears in the tree and select it
      const codeStr = newPosition.code_str ?? (newPosition.code ? `#${newPosition.code}` : "");
      treePositions.value = [
        {
          id: newPosition.id,
          name: newPosition.name ?? name,
          code_str: codeStr,
          code: newPosition.code,
          roles_count: 0,
          responsibilities_count: 0,
          ...pickTreeActivationFields(newPosition),
        },
        ...treePositions.value,
      ];
      treePositionsTotal.value = (treePositionsTotal.value ?? 0) + 1;
      await buildTreeData();
      await proxy.$nextTick();

      const addedItem = findInTree(treeData.value, String(newPosition.id), "position");
      if (addedItem) {
        handleTreeItemSelected({
          id: addedItem.id,
          type: addedItem.type,
          name: addedItem.name,
          data: addedItem,
        });
        updateRouteForTreeItem(addedItem.id, "position");
      }

      proxy.$notifications?.success?.(
        "Position created",
        `Position "${name}" was added and selected in the tree.`,
        3000
      );
    }
  } catch (error) {
    console.error("Error creating position from tree view:", error);
    proxy.$notifications?.error?.(
      "Could not create position",
      error?.message || "Please try again."
    );
  } finally {
    isPaneAddPositionSaving.value = false;
    proxy.$loader?.hide();
  }
};

/** Right pane hit-test for the detail column (used to adjust drag cursor vs tree). */
function pointerInPaneRight(clientX, clientY) {
  const el = paneViewRightRef.value;
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
}

// Checkpoint drag: start – hide native cursor everywhere, show only DragAndDropInPositionsTreeNavigatorCursor
function handleCheckpointDragStartFromTable({
  sourceResponsibilityId,
  checkpointId,
  checkpointName,
  clientX = 0,
  clientY = 0,
}) {
  isCheckpointDragging.value = true;
  checkpointDragSource.value = { sourceResponsibilityId, checkpointId, checkpointName };
  checkpointDragCursorState.value = "Dragging";
  dragCursorX.value = clientX;
  dragCursorY.value = clientY;
  document.documentElement.classList.add("checkpoint-drag-cursor-active");
  document.body.classList.add("checkpoint-drag-cursor-active");
  const transparentCursor =
    'url(\'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/%3E\') 0 0, none';
  document.documentElement.style.cursor = transparentCursor;
  document.body.style.cursor = transparentCursor;
  const onDragover = (e) => {
    dragCursorX.value = e.clientX;
    dragCursorY.value = e.clientY;
    if (pointerInPaneRight(e.clientX, e.clientY)) {
      checkpointDragCursorState.value = "Dragging";
    }
  };
  const onEnd = () => {
    isCheckpointDragging.value = false;
    checkpointDragSource.value = null;
    document.documentElement.classList.remove("checkpoint-drag-cursor-active");
    document.body.classList.remove("checkpoint-drag-cursor-active");
    document.documentElement.style.cursor = "";
    document.body.style.cursor = "";
    document.removeEventListener("dragover", onDragover);
    document.removeEventListener("dragend", onEnd);
    dragEndCleanup = null;
  };
  document.addEventListener("dragover", onDragover);
  document.addEventListener("dragend", onEnd, { once: true });
  dragEndCleanup = onEnd;
}

function handleCheckpointDragEndFromTable() {
  if (dragEndCleanup) dragEndCleanup();
  isCheckpointDragging.value = false;
  checkpointDragSource.value = null;
  document.documentElement.classList.remove("checkpoint-drag-cursor-active");
  document.body.classList.remove("checkpoint-drag-cursor-active");
  document.documentElement.style.cursor = "";
  document.body.style.cursor = "";
}

function handleCheckpointDragOverState({ allowed }) {
  const next = allowed ? "Allowed" : "Blocked";
  if (checkpointDragCursorState.value === next) return;
  checkpointDragCursorState.value = next;
}

function setPositionTreeDragCursorVars(state) {
  const fill = state === "Allowed" ? "#DBFAE6" : state === "Blocked" ? "#D92D20" : "#ffffff";
  const labelColor = state === "Blocked" ? "#D92D20" : "#101828";
  document.documentElement.style.setProperty("--position-tree-drag-hand-fill", fill);
  document.documentElement.style.setProperty("--position-tree-drag-label-color", labelColor);
}

function clearPositionTreeDragCursorVars() {
  document.documentElement.style.removeProperty("--position-tree-drag-hand-fill");
  document.documentElement.style.removeProperty("--position-tree-drag-label-color");
}

function handleResponsibilityDragOverState({ allowed }) {
  const next = allowed ? "Allowed" : "Blocked";
  if (responsibilityDragCursorState.value === next) return;
  responsibilityDragCursorState.value = next;
  setPositionTreeDragCursorVars(next);
}

function handleRoleDragOverState({ allowed }) {
  const next = allowed ? "Allowed" : "Blocked";
  if (roleDragCursorState.value === next) return;
  roleDragCursorState.value = next;
  setPositionTreeDragCursorVars(next);
}

function setResponsibilityDragAttachUiActive(active) {
  const transparentCursor =
    'url(\'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/%3E\') 0 0, none';
  if (active) {
    isResponsibilityToRoleDragging.value = true;
    responsibilityDragCursorState.value = "Dragging";
    setPositionTreeDragCursorVars("Dragging");
    document.documentElement.classList.add("checkpoint-drag-cursor-active");
    document.body.classList.add("checkpoint-drag-cursor-active");
    document.documentElement.style.cursor = transparentCursor;
    document.body.style.cursor = transparentCursor;
  } else {
    isResponsibilityToRoleDragging.value = false;
    document.documentElement.classList.remove("checkpoint-drag-cursor-active");
    document.body.classList.remove("checkpoint-drag-cursor-active");
    document.documentElement.style.cursor = "";
    document.body.style.cursor = "";
    clearPositionTreeDragCursorVars();
  }
}

function handleResponsibilityDragStartFromTable({
  sourceRoleId,
  responsibilityId,
  responsibilityName,
  clientX = 0,
  clientY = 0,
  deferAttachUi = false,
}) {
  responsibilityDragAttachmentPrefetchGen += 1;
  const respPrefetchGen = responsibilityDragAttachmentPrefetchGen;
  void prefetchRolesHavingResponsibilityAttachedForDrag(respPrefetchGen, responsibilityId);
  responsibilityDragSessionActive.value = true;
  responsibilityDragSource.value = { sourceRoleId, responsibilityId, responsibilityName };
  dragCursorX.value = clientX;
  dragCursorY.value = clientY;
  setResponsibilityDragAttachUiActive(!deferAttachUi);
  let dragCursorRafId = 0;
  let lastDragPointerEvent = null;
  const flushDragCursor = () => {
    dragCursorRafId = 0;
    const e = lastDragPointerEvent;
    if (!e) return;
    dragCursorX.value = e.clientX;
    dragCursorY.value = e.clientY;
    if (deferAttachUi) {
      setResponsibilityDragAttachUiActive(!pointerInPaneRight(e.clientX, e.clientY));
    } else if (pointerInPaneRight(e.clientX, e.clientY)) {
      if (responsibilityDragCursorState.value !== "Dragging") {
        responsibilityDragCursorState.value = "Dragging";
        setPositionTreeDragCursorVars("Dragging");
      }
    }
  };
  const onDragover = (e) => {
    lastDragPointerEvent = e;
    if (!dragCursorRafId) {
      dragCursorRafId = requestAnimationFrame(flushDragCursor);
    }
  };
  const onEnd = () => {
    if (dragCursorRafId) cancelAnimationFrame(dragCursorRafId);
    responsibilityDragSessionActive.value = false;
    responsibilityDragSource.value = null;
    setResponsibilityDragAttachUiActive(false);
    document.removeEventListener("dragover", onDragover);
    document.removeEventListener("dragend", onEnd);
    responsibilityDragEndCleanup = null;
  };
  document.addEventListener("dragover", onDragover);
  document.addEventListener("dragend", onEnd, { once: true });
  responsibilityDragEndCleanup = onEnd;
}

function handleResponsibilityDragEndFromTable() {
  responsibilityDragAttachmentPrefetchGen += 1;
  if (responsibilityDragEndCleanup) responsibilityDragEndCleanup();
  responsibilityDragSessionActive.value = false;
  responsibilityDragSource.value = null;
  setResponsibilityDragAttachUiActive(false);
}

function startRoleDragLeftPanelBulkPrefetch() {
  if (roleDragLeftBulkPrefetchStarted) return;
  roleDragLeftBulkPrefetchStarted = true;
  const gen = roleDragAttachmentPrefetchGen;
  void prefetchPanePositionAttachedRoleIdsForDrag(gen, { includeAllPositions: true });
}

function setRoleDragAttachUiActive(active) {
  const transparentCursor =
    'url(\'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/%3E\') 0 0, none';
  if (active) {
    startRoleDragLeftPanelBulkPrefetch();
    isRoleToPositionDragging.value = true;
    roleDragCursorState.value = "Dragging";
    setPositionTreeDragCursorVars("Dragging");
    document.documentElement.classList.add("checkpoint-drag-cursor-active");
    document.body.classList.add("checkpoint-drag-cursor-active");
    document.documentElement.style.cursor = transparentCursor;
    document.body.style.cursor = transparentCursor;
  } else {
    isRoleToPositionDragging.value = false;
    document.documentElement.classList.remove("checkpoint-drag-cursor-active");
    document.body.classList.remove("checkpoint-drag-cursor-active");
    document.documentElement.style.cursor = "";
    document.body.style.cursor = "";
    clearPositionTreeDragCursorVars();
  }
}

function handleRoleDragStartFromTable({
  sourcePositionId,
  roleId,
  roleName,
  clientX = 0,
  clientY = 0,
  deferAttachUi = false,
}) {
  roleDragAttachmentPrefetchGen += 1;
  roleDragLeftBulkPrefetchStarted = false;
  const prefetchGen = roleDragAttachmentPrefetchGen;
  void prefetchPanePositionAttachedRoleIdsForDrag(prefetchGen, {
    includeAllPositions: !deferAttachUi,
  });
  roleDragSessionActive.value = true;
  roleDragSource.value = { sourcePositionId, roleId, roleName };
  dragCursorX.value = clientX;
  dragCursorY.value = clientY;
  setRoleDragAttachUiActive(!deferAttachUi);
  let dragCursorRafId = 0;
  let lastDragPointerEvent = null;
  const flushDragCursor = () => {
    dragCursorRafId = 0;
    const e = lastDragPointerEvent;
    if (!e) return;
    dragCursorX.value = e.clientX;
    dragCursorY.value = e.clientY;
    if (deferAttachUi) {
      setRoleDragAttachUiActive(!pointerInPaneRight(e.clientX, e.clientY));
    } else if (pointerInPaneRight(e.clientX, e.clientY)) {
      if (roleDragCursorState.value !== "Dragging") {
        roleDragCursorState.value = "Dragging";
        setPositionTreeDragCursorVars("Dragging");
      }
    }
  };
  const onDragover = (e) => {
    lastDragPointerEvent = e;
    if (!dragCursorRafId) {
      dragCursorRafId = requestAnimationFrame(flushDragCursor);
    }
  };
  const onEnd = () => {
    if (dragCursorRafId) cancelAnimationFrame(dragCursorRafId);
    roleDragSessionActive.value = false;
    roleDragSource.value = null;
    setRoleDragAttachUiActive(false);
    document.removeEventListener("dragover", onDragover);
    document.removeEventListener("dragend", onEnd);
    roleDragEndCleanup = null;
  };
  document.addEventListener("dragover", onDragover);
  document.addEventListener("dragend", onEnd, { once: true });
  roleDragEndCleanup = onEnd;
}

function handleRoleDragEndFromTable() {
  roleDragAttachmentPrefetchGen += 1;
  roleDragLeftBulkPrefetchStarted = false;
  if (roleDragEndCleanup) roleDragEndCleanup();
  roleDragSessionActive.value = false;
  isRoleToPositionDragging.value = false;
  roleDragSource.value = null;
  setRoleDragAttachUiActive(false);
}

function handleResponsibilityDroppedOnTree(payload) {
  if (!payload?.responsibilityId || payload.targetRoleId == null) return;
  const respLabel =
    payload.responsibilityCodeStr ||
    (payload.responsibilityName
      ? String(payload.responsibilityName)
      : `Responsibility #${payload.responsibilityId}`);
  const roleLabel = payload.targetRoleName || `Role #${payload.targetRoleId}`;
  pendingResponsibilityAttach.value = {
    responsibilityId: payload.responsibilityId,
    sourceRoleId: payload.sourceRoleId,
    targetRoleId: payload.targetRoleId,
    targetPositionId: payload.targetPositionId ?? null,
    targetRoleName: payload.targetRoleName || "",
    responsibilityName: payload.responsibilityName || "",
    responsibilityCodeStr: payload.responsibilityCodeStr || "",
    responsibilityLabel: respLabel,
    targetRoleLabel: roleLabel,
  };
  showDropToAttachResponsibilityDialog.value = true;
}

function handleCloseDropToAttachResponsibilityDialog() {
  showDropToAttachResponsibilityDialog.value = false;
  pendingResponsibilityAttach.value = null;
}

function onDropToAttachResponsibilityDialogInput(open) {
  if (!open) handleCloseDropToAttachResponsibilityDialog();
}

async function handleConfirmDropToAttachResponsibility() {
  const pending = pendingResponsibilityAttach.value;
  if (!pending) return;
  const accountIdStr = localStorage.getItem("currentAccountID");
  const accountIdParsed = accountIdStr ? JSON.parse(accountIdStr) : null;
  if (!accountIdParsed || pending.responsibilityId == null || pending.targetRoleId == null) return;
  try {
    proxy.$loader?.show();
    showDropToAttachResponsibilityDialog.value = false;
    await attachResponsibilityToRole(accountIdParsed, pending.targetRoleId, {
      attachment_id: pending.responsibilityId,
    });
    mergeResponsibilityOntoRoleResponsibilityCache(pending.targetRoleId, pending.responsibilityId);
    const attachNavContext = { ...pending };
    await handlePaneTreeRefresh();
    roleAttachRefreshKey.value += 1;
    const respPart =
      pending.responsibilityCodeStr ||
      (pending.responsibilityName
        ? `${pending.responsibilityName}`
        : String(pending.responsibilityId));
    const rolePart = pending.targetRoleName || String(pending.targetRoleId);
    proxy.$notifications?.success(
      "Responsibility successfully attached",
      `Responsibility ${respPart} was attached to role ${rolePart}`,
      5000
    );
    pendingResponsibilityAttach.value = null;
    await proxy.$nextTick();
    await selectPaneTreeAfterResponsibilityAttachedToRole(attachNavContext);
  } catch (error) {
    console.error("Error attaching responsibility to role:", error);
    proxy.$notifications?.error("Failed to attach responsibility");
  } finally {
    proxy.$loader?.hide();
  }
}

function formatRoleAttachDialogLabel({ roleCodeStr, roleName, roleId }) {
  const code = String(roleCodeStr || "").trim();
  const name = String(roleName || "").trim();
  if (code && name) return `${code} - ${name}`;
  if (name) return name;
  if (code) return code;
  return roleId != null ? `Role #${roleId}` : "";
}

/** After attaching a role to a position, select that position in the left pane (Position detail + attached roles). */
async function selectPanePositionInTreeAfterRoleAttached(positionId, positionName) {
  if (currentView.value !== "Pane" || positionId == null) return;
  const posIdStr = String(positionId);
  await proxy.$nextTick();
  const posNode = findInTree(treeData.value, posIdStr, "position");
  if (posNode) {
    await handleTreeItemSelected({
      id: posNode.id,
      type: posNode.type,
      name: posNode.name || positionName || `Position #${posIdStr}`,
      data: posNode,
      skipExpandPositionRoles: true,
      panePositionTab: "attached-roles",
    });
    return;
  }
  await applyTreeItemSelection(
    {
      id: posIdStr,
      type: "position",
      name: positionName || `Position #${posIdStr}`,
      data: { id: posIdStr, name: positionName || "", type: "position" },
      skipExpandPositionRoles: true,
      panePositionTab: "attached-roles",
    },
    null
  );
}

function handleRoleDroppedOnTree(payload) {
  if (!payload?.roleId || payload.targetPositionId == null) return;
  const roleLabel = formatRoleAttachDialogLabel({
    roleCodeStr: payload.roleCodeStr,
    roleName: payload.roleName,
    roleId: payload.roleId,
  });
  const posLabel = payload.targetPositionName || `Position #${payload.targetPositionId}`;
  pendingRoleAttach.value = {
    roleId: payload.roleId,
    sourcePositionId: payload.sourcePositionId,
    targetPositionId: payload.targetPositionId,
    targetPositionName: payload.targetPositionName || "",
    roleName: payload.roleName || "",
    roleCodeStr: payload.roleCodeStr || "",
    roleLabel,
    targetPositionLabel: posLabel,
  };
  showDropToAttachRoleDialog.value = true;
}

function handleCloseDropToAttachRoleDialog() {
  showDropToAttachRoleDialog.value = false;
  pendingRoleAttach.value = null;
}

function onDropToAttachRoleDialogInput(open) {
  if (!open) handleCloseDropToAttachRoleDialog();
}

async function handleConfirmDropToAttachRole() {
  const pending = pendingRoleAttach.value;
  if (!pending) return;
  const accountIdStr = localStorage.getItem("currentAccountID");
  const accountIdParsed = accountIdStr ? JSON.parse(accountIdStr) : null;
  if (!accountIdParsed || pending.roleId == null || pending.targetPositionId == null) return;
  try {
    proxy.$loader?.show();
    showDropToAttachRoleDialog.value = false;
    await attachRoleToPosition(accountIdParsed, pending.targetPositionId, {
      attachment_id: pending.roleId,
    });
    mergeRoleIdIntoPanePositionAttachedRoles(pending.targetPositionId, pending.roleId);
    await handlePaneTreeRefresh();
    roleAttachRefreshKey.value += 1;
    const rolePart = formatRoleAttachDialogLabel({
      roleCodeStr: pending.roleCodeStr,
      roleName: pending.roleName,
      roleId: pending.roleId,
    });
    const posPart = pending.targetPositionName || String(pending.targetPositionId);
    proxy.$notifications?.success(
      "Role successfully attached",
      `Role ${rolePart} was attached to position ${posPart}`,
      5000
    );
    const attachedToPositionId = pending.targetPositionId;
    const attachedToPositionName = pending.targetPositionName || "";
    pendingRoleAttach.value = null;
    await selectPanePositionInTreeAfterRoleAttached(attachedToPositionId, attachedToPositionName);
  } catch (error) {
    console.error("Error attaching role to position:", error);
    proxy.$notifications?.error("Failed to attach role to position");
  } finally {
    proxy.$loader?.hide();
  }
}

// Drop on tree: open confirmation dialog first (DropToMoveCheckpointDialog)
function handleCheckpointDroppedOnTree({
  checkpointId,
  sourceResponsibilityId,
  targetResponsibilityId,
  targetResponsibilityName,
  checkpointName = "",
}) {
  pendingDropMove.value = {
    checkpointId,
    sourceResponsibilityId,
    targetResponsibilityId,
    targetResponsibilityName: targetResponsibilityName || "",
    checkpointName,
  };
  showDropToMoveDialog.value = true;
}

function handleCloseDropToMoveDialog() {
  showDropToMoveDialog.value = false;
  pendingDropMove.value = null;
}

// After user confirms in dialog: call move API, then toast and refresh
async function handleConfirmDropToMove() {
  const pending = pendingDropMove.value;
  if (!pending) return;
  const accountIdStr = localStorage.getItem("currentAccountID");
  const accountId = accountIdStr ? JSON.parse(accountIdStr) : null;
  if (!accountId || !pending.checkpointId || pending.targetResponsibilityId == null) return;
  try {
    proxy.$loader?.show();
    showDropToMoveDialog.value = false;
    await moveCheckpointOnResponsibility(accountId, pending.checkpointId, {
      responsibility_id: pending.targetResponsibilityId,
    });
    await handlePaneTreeRefresh();
    checkpointMoveRefreshKey.value += 1;
    proxy.$notifications?.success(
      "Checkpoint successfully moved",
      `Checkpoint ${pending.checkpointName || ""} was moved to responsibility ${pending.targetResponsibilityName || ""}`,
      5000
    );
    pendingDropMove.value = null;
  } catch (error) {
    console.error("Error moving checkpoint:", error);
    proxy.$notifications?.error("Failed to move checkpoint");
  } finally {
    proxy.$loader?.hide();
  }
}

// Pane tree: expand/collapse to match selection (single expanded position; auto-expand role → responsibilities)
const expandPaneTreeForSelection = async (itemData, numericId, itemType) => {
  if (currentView.value !== "Pane") return;
  const nav = positionsSearchNavigateRef.value;
  if (!nav?.applyPositionSelectionExpand || !nav?.applyRoleSelectionExpand) {
    return;
  }

  if (itemType === "position") {
    const pos =
      itemData.data?.type === "position"
        ? itemData.data
        : findInTree(treeData.value, numericId, "position");
    const posIdStr = numericId != null ? String(numericId) : "";
    const knownRoleIds = posIdStr ? panePositionAttachedRoleIds.value[posIdStr] : null;
    const canExpand =
      pos?.hasRolesOrResponsibilities === true ||
      (Array.isArray(knownRoleIds) && knownRoleIds.length > 0);
    const skipRoleBranch = itemData.skipExpandPositionRoles === true;
    const shouldExpandSelf = !skipRoleBranch && canExpand;
    // Always collapse other positions; previously we returned early when nothing to attach,
    // which left other positions (and their roles) visually expanded.
    nav.applyPositionSelectionExpand(String(numericId), {
      expandSelf: shouldExpandSelf,
    });
    await proxy.$nextTick();
    if (shouldExpandSelf) {
      await handleTreeToggleExpand({
        itemId: numericId,
        itemType: "position",
        isExpanded: true,
      });
    }
    return;
  }

  if (itemType === "role") {
    const parentPosId =
      itemData.parentPositionId != null
        ? String(itemData.parentPositionId)
        : findPathToItem(treeData.value, numericId, "role")?.[0]?.id;
    if (!parentPosId) return;

    let roleNode =
      itemData.data?.type === "role"
        ? itemData.data
        : findRoleUnderPosition(treeData.value, parentPosId, numericId) ||
          findInTree(treeData.value, numericId, "role");

    nav.applyPositionSelectionExpand(parentPosId);
    await proxy.$nextTick();
    await handleTreeToggleExpand({
      itemId: parentPosId,
      itemType: "position",
      isExpanded: true,
      skipRouteUpdate: true,
    });
    await proxy.$nextTick();

    if (!roleNode) {
      roleNode = findRoleUnderPosition(treeData.value, parentPosId, numericId);
    }

    const canExpandRole = roleNode?.hasResponsibilities === true;
    nav.applyRoleSelectionExpand(parentPosId, String(numericId), {
      expandSelf: canExpandRole,
    });
    await proxy.$nextTick();
    if (canExpandRole) {
      await handleTreeToggleExpand({
        itemId: numericId,
        itemType: "role",
        isExpanded: true,
        parentId: parentPosId,
        skipRouteUpdate: true,
      });
    }
    return;
  }

  if (itemType === "responsibility") {
    const path = findPathToItem(treeData.value, numericId, "responsibility");
    const parentPosId =
      itemData.parentPositionId != null ? String(itemData.parentPositionId) : path?.[0]?.id;
    const parentRoleId =
      itemData.parentRoleId != null ? String(itemData.parentRoleId) : path?.[1]?.id;
    if (!parentPosId) {
      return;
    }

    nav.applyPositionSelectionExpand(parentPosId);
    await proxy.$nextTick();
    await handleTreeToggleExpand({
      itemId: parentPosId,
      itemType: "position",
      isExpanded: true,
      skipRouteUpdate: true,
    });
    await proxy.$nextTick();

    // Direct-to-position responsibilities have no parent role — they sit under the position.
    if (!parentRoleId) {
      return;
    }

    let roleNode =
      itemData.data?.type === "role"
        ? itemData.data
        : findRoleUnderPosition(treeData.value, parentPosId, parentRoleId);

    if (!roleNode) {
      roleNode = findRoleUnderPosition(treeData.value, parentPosId, parentRoleId);
    }
    if (!roleNode) {
      await ensureRoleNodeUnderPosition(parentPosId, parentRoleId);
      await proxy.$nextTick();
      roleNode = findRoleUnderPosition(treeData.value, parentPosId, parentRoleId);
    }

    // Always load responsibilities for this role: counts on the role node can be 0
    // while the responsibility still exists (API / lazy tree mismatch).
    nav.applyRoleSelectionExpand(parentPosId, String(parentRoleId));
    await proxy.$nextTick();
    await handleTreeToggleExpand({
      itemId: parentRoleId,
      itemType: "role",
      isExpanded: true,
      parentId: parentPosId,
      skipRouteUpdate: true,
    });
  }
};

/** Same id/type rules as tree clicks: legacy composite keys are "positionId-roleId" (two numeric segments). */
const computeSelectionIdAndType = (itemData) => {
  let actualId = itemData.id;

  if (typeof actualId === "string" && actualId.includes("-")) {
    const parts = actualId.split("-");
    if (parts.length === 2 && /^\d+$/.test(parts[0]) && /^\d+$/.test(parts[1])) {
      actualId = parts[1];
    }
  }

  const numericId =
    typeof actualId === "string" && !isNaN(actualId) ? parseInt(actualId, 10) : actualId;

  const itemType = (itemData.type || "").toLowerCase();
  return { numericId, itemType };
};

const buildSelectedItemState = (itemData, numericId, itemType) => {
  const state = {
    // Tree nodes use string ids; keep selection aligned so left-panel highlight matches after flat pick.
    id: numericId != null && numericId !== "" ? String(numericId) : numericId,
    type: itemType,
    name: itemData.name || "",
    data: itemData.data || itemData,
  };
  const pp = itemData.parentPositionId;
  const pr = itemData.parentRoleId;
  if (itemType === "role" && pp != null && pp !== "") {
    state.parentPositionId = pp;
  }
  if (itemType === "responsibility") {
    if (pr != null && pr !== "") state.parentRoleId = pr;
    if (pp != null && pp !== "") state.parentPositionId = pp;
  }
  if (itemType === "position") {
    if (itemData.skipExpandPositionRoles === true) {
      state.skipExpandPositionRoles = true;
    }
    if (itemData.panePositionTab != null && itemData.panePositionTab !== "") {
      state.panePositionTab = itemData.panePositionTab;
    }
  }
  return state;
};

/** Build `name=` query for position list API — prefer human name, else numeric code so the list collapses toward one row. */
function derivePositionListNameScopeQuery(positionRecord, positionIdStr) {
  if (!positionRecord && positionIdStr == null) return "";
  const nm = typeof positionRecord?.name === "string" ? positionRecord.name.trim() : "";
  if (nm.length >= 2) return nm;
  const codeRaw =
    positionRecord?.code ??
    (typeof positionRecord?.code_str === "string"
      ? positionRecord.code_str.replace(/^#/, "").trim()
      : "");
  if (codeRaw !== "" && codeRaw != null) return String(codeRaw);
  return "";
}

/**
 * Reload pane tree positions so only the anchor position row remains (matches flat-search → tree UX).
 * Uses position list API `name=` when available, then keeps exactly the resolved position row.
 */
async function narrowPaneTreeToAnchorPosition(positionIdStr, positionRecord) {
  if (positionIdStr == null || positionIdStr === "") return false;

  const accountIdStr = localStorage.getItem("currentAccountID");
  const accountId = accountIdStr ? JSON.parse(accountIdStr) : null;
  if (!accountId) return false;

  let record = positionRecord && positionRecord.id != null ? positionRecord : null;
  if (!record) {
    try {
      const r = await getPositionId(accountId, positionIdStr);
      record = r?.data ?? null;
    } catch (e) {
      console.error("narrowPaneTreeToAnchorPosition getPositionId:", e);
      return false;
    }
  }
  if (!record?.id) return false;

  const nameQuery = derivePositionListNameScopeQuery(record, positionIdStr);
  applyPaneFiltersToStore();

  try {
    let chosen = null;
    if (nameQuery) {
      paneTreePositionListNameScope.value = nameQuery;
      const queryParams = getPanePositionQueryParams(TREE_PAGE_SIZE, 0);
      const response = await getListOfPositionsV2({
        account_id: accountId,
        queryParamsObj: queryParams,
      });
      const results = response?.data?.results || [];
      chosen = results.find((p) => String(p.id) === String(positionIdStr)) ?? null;
    }

    if (!chosen) {
      paneTreePositionListNameScope.value = null;
      chosen = record;
    }

    treePositions.value = [chosen];
    treePositionsTotal.value = 1;
    await buildTreeData();
    await proxy.$nextTick();
    return true;
  } catch (e) {
    console.error("narrowPaneTreeToAnchorPosition:", e);
    paneTreePositionListNameScope.value = null;
    return false;
  }
}

/**
 * Paginate pane positions until `positionId` is loaded. User-driven "load more" is skipped while the
 * search box has text (see `loadMoreTreePositions`), but flat-search picks still need the anchor
 * position row in the DOM before we can expand and scroll to role/responsibility rows.
 */
async function ensurePaneTreeContainsPosition(positionIdStr) {
  if (positionIdStr == null || positionIdStr === "") return;
  const target = String(positionIdStr);
  if (treePositions.value.some((p) => String(p.id) === target)) return;

  const accountIdStr = localStorage.getItem("currentAccountID");
  const accountId = accountIdStr ? JSON.parse(accountIdStr) : null;
  if (!accountId) return;

  let batches = 0;
  while (batches < 80 && treePositions.value.length < treePositionsTotal.value) {
    if (treePositions.value.some((p) => String(p.id) === target)) return;

    batches += 1;
    const offset = treePositions.value.length;
    const queryParams = getPanePositionQueryParams(TREE_PAGE_SIZE, offset);
    let results = [];
    try {
      const response = await getListOfPositionsV2({
        account_id: accountId,
        queryParamsObj: queryParams,
      });
      results = response?.data?.results || [];
      const total = response?.data?.count ?? treePositionsTotal.value;
      treePositionsTotal.value = total;
      treePositions.value = [...treePositions.value, ...results];
      await buildTreeData();
      await proxy.$nextTick();
    } catch (e) {
      console.error("ensurePaneTreeContainsPosition:", e);
      return;
    }
    if (results.length === 0) return;
  }
}

/**
 * Bring the selected tree row into the left pane viewport. Native `scrollIntoView` often fails here
 * (flat-search → tree swap, nested overflow), so we adjust `paneLeftRef.scrollTop` explicitly and retry
 * until expanded/lazy rows have painted.
 */
async function scrollPaneTreeRowIntoView(numericId, itemType, opts = {}) {
  const emphasize = opts.emphasize ?? "center";
  const topMargin = opts.topMargin ?? 36;
  const container = paneLeftRef.value;
  if (!container) return;

  const selector = `[data-tree-item-id="${String(numericId)}"][data-tree-item-type="${itemType}"]`;

  const adjustScrollForEl = (el) => {
    const elRect = el.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();
    if (elRect.height < 1 && elRect.width < 1) return false;

    let delta = 0;
    if (emphasize === "nearest") {
      const margin = opts.nearestMargin ?? 8;
      if (elRect.bottom > cRect.bottom - margin) {
        delta = elRect.bottom - (cRect.bottom - margin);
      } else if (elRect.top < cRect.top + margin) {
        delta = elRect.top - (cRect.top + margin);
      }
    } else if (emphasize === "start") {
      delta = elRect.top - cRect.top - topMargin;
    } else {
      delta = elRect.top + elRect.height / 2 - (cRect.top + cRect.height / 2);
    }

    const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
    container.scrollTop = Math.min(maxScroll, Math.max(0, container.scrollTop + delta));
    return true;
  };

  const runRetryLoop = async () => {
    for (let attempt = 0; attempt < 18; attempt++) {
      await proxy.$nextTick();
      const el = container.querySelector(selector);
      if (el) {
        adjustScrollForEl(el);
        await new Promise((r) => requestAnimationFrame(r));
        const el2 = container.querySelector(selector);
        if (el2) adjustScrollForEl(el2);
        return true;
      }
      await new Promise((r) => requestAnimationFrame(r));
    }
    return false;
  };

  await runRetryLoop();

  await new Promise((r) => setTimeout(r, 50));
  const elLate = container.querySelector(selector);
  if (elLate) adjustScrollForEl(elLate);

  await new Promise((r) => setTimeout(r, 120));
  const elLater = container.querySelector(selector);
  if (elLater) adjustScrollForEl(elLater);
}

const applyTreeItemSelection = async (itemData, preserveViewportTop, options = {}) => {
  paneNoPositionDetail.value = false;
  paneAttachedRolesSearchBackup.value = "";

  const { numericId, itemType } = computeSelectionIdAndType(itemData);

  let anchorPositionId = null;
  if (itemType === "position") {
    anchorPositionId = numericId != null && numericId !== "" ? String(numericId) : null;
  } else if (itemData.parentPositionId != null && itemData.parentPositionId !== "") {
    anchorPositionId = String(itemData.parentPositionId);
  } else if (proxy?.$route?.query?.positionId != null) {
    anchorPositionId = String(proxy.$route.query.positionId);
  }

  const narrowToAnchor =
    options.narrowTreeToAnchorPosition === true &&
    anchorPositionId &&
    (itemType === "responsibility" || itemType === "role" || itemType === "position");

  if (narrowToAnchor) {
    const ok = await narrowPaneTreeToAnchorPosition(
      anchorPositionId,
      options.anchorPositionRecord ?? null
    );
    if (!ok && anchorPositionId) {
      await ensurePaneTreeContainsPosition(anchorPositionId);
    }
  } else if (anchorPositionId) {
    await ensurePaneTreeContainsPosition(anchorPositionId);
  }

  selectedItem.value = buildSelectedItemState(itemData, numericId, itemType);

  updateRouteForTreeItem(numericId, itemType);

  await expandPaneTreeForSelection(itemData, numericId, itemType);

  if (itemType === "responsibility") {
    const pp = itemData.parentPositionId != null ? String(itemData.parentPositionId) : null;
    const pr = itemData.parentRoleId != null ? String(itemData.parentRoleId) : null;
    if (pp && pr) {
      ensureResponsibilityNodeUnderRole(pp, pr, itemData, numericId);
      await proxy.$nextTick();
      const navRef = positionsSearchNavigateRef.value;
      if (navRef?.expandNodes) {
        navRef.expandNodes([pp, `${pp}-${pr}`]);
      }
      await proxy.$nextTick();
    }
  }

  // Path exists in tree only after lazy load; set role/res/responsibility query when first call skipped
  updateRouteForTreeItem(numericId, itemType);

  const scrollAfterFlatReveal = options.scrollSelectedIntoView === true;
  const shouldScrollTreeRow =
    preserveViewportTop == null && (scrollAfterFlatReveal || itemType === "responsibility");

  if (shouldScrollTreeRow) {
    await scrollPaneTreeRowIntoView(numericId, itemType, {
      emphasize: scrollAfterFlatReveal ? "start" : "nearest",
      topMargin: scrollAfterFlatReveal ? 40 : 8,
      nearestMargin: 8,
    });
  }

  if (preserveViewportTop != null) {
    await proxy.$nextTick();
    await proxy.$nextTick();
    requestAnimationFrame(() => {
      const container = paneLeftRef.value;
      if (!container) return;
      const el = container.querySelector(
        `[data-tree-item-id="${String(numericId)}"][data-tree-item-type="${itemType}"]`
      );
      if (!el) return;
      const afterTop = el.getBoundingClientRect().top;
      container.scrollTop += preserveViewportTop - afterTop;
    });
  }
};

// Handle tree item selection
const handleTreeItemSelected = async (itemData) => {
  if (itemData.fromFlatSearch) {
    const { numericId, itemType } = computeSelectionIdAndType(itemData);
    paneNoPositionDetail.value = false;
    selectedItem.value = buildSelectedItemState(itemData, numericId, itemType);
    await proxy.$nextTick();

    // TreeItem emits data: row object; API fields for search hits live on row.raw (not on the row wrapper).
    const flatRawOrData = itemData.raw ?? itemData.data?.raw ?? itemData.data;
    const resolved = await resolveAncestorsForFlatSelection(
      itemData.type.toLowerCase(),
      itemData.id,
      flatRawOrData
    );
    const baseRaw = itemData.raw ?? itemData.data?.raw ?? flatRawOrData;
    const tLower = itemData.type?.toLowerCase();
    let mergedRaw = baseRaw;
    if (tLower === "responsibility" && resolved?.responsibilityRecord) {
      mergedRaw = {
        ...(baseRaw && typeof baseRaw === "object" ? baseRaw : {}),
        ...resolved.responsibilityRecord,
      };
    }
    const merged = {
      ...itemData,
      fromFlatSearch: false,
      parentPositionId: resolved?.positionId ?? itemData.parentPositionId,
      parentRoleId: resolved?.roleId ?? itemData.parentRoleId,
      raw: mergedRaw,
    };
    if (itemData.data && typeof itemData.data === "object") {
      merged.data = { ...itemData.data, raw: mergedRaw };
    }

    // Sync selection before the pane switches from flat results to the tree (`paneRevealTreeAfterFlatPick`).
    // Otherwise TreeItem renders for a tick without parent ids / matching route.resId — highlight and URL can desync for responsibilities.
    {
      const { numericId: mergedNumId, itemType: mergedTyp } = computeSelectionIdAndType(merged);
      selectedItem.value = buildSelectedItemState(merged, mergedNumId, mergedTyp);
      await proxy.$nextTick();
    }

    const t = merged.type?.toLowerCase();

    let anchorPositionRecordForNarrow = resolved?.positionRaw ?? null;
    if (
      (t === "responsibility" || t === "role") &&
      resolved?.positionId != null &&
      (anchorPositionRecordForNarrow == null || !anchorPositionRecordForNarrow?.id)
    ) {
      const accountIdStr = localStorage.getItem("currentAccountID");
      const accountIdParsed = accountIdStr ? JSON.parse(accountIdStr) : null;
      if (accountIdParsed) {
        try {
          const r = await getPositionId(accountIdParsed, resolved.positionId);
          anchorPositionRecordForNarrow = r?.data ?? null;
        } catch (e) {
          console.error("handleTreeItemSelected getPositionId:", e);
        }
      }
    }

    if (
      t === "position" &&
      merged.raw &&
      !treePositions.value.some((p) => String(p.id) === String(merged.id))
    ) {
      treePositions.value = [merged.raw, ...treePositions.value];
      treePositionsTotal.value = (treePositionsTotal.value ?? 0) + 1;
      await buildTreeData();
      await proxy.$nextTick();
    } else if (
      t === "role" &&
      resolved?.positionId != null &&
      !treePositions.value.some((p) => String(p.id) === String(resolved.positionId))
    ) {
      let positionPayload = resolved.positionRaw;
      if (!positionPayload) {
        const accountIdStr = localStorage.getItem("currentAccountID");
        const accountIdParsed = accountIdStr ? JSON.parse(accountIdStr) : null;
        if (accountIdParsed) {
          try {
            const r = await getPositionId(accountIdParsed, resolved.positionId);
            positionPayload = r?.data;
          } catch (e) {
            console.error("handleTreeItemSelected getPositionId:", e);
          }
        }
      }
      if (positionPayload?.id != null) {
        treePositions.value = [positionPayload, ...treePositions.value];
        treePositionsTotal.value = (treePositionsTotal.value ?? 0) + 1;
        await buildTreeData();
        await proxy.$nextTick();
      }
    }

    paneRevealTreeAfterFlatPick.value = true;
    paneSearchFlatRows.value = [];
    await proxy.$nextTick();
    await proxy.$nextTick();
    await applyTreeItemSelection(merged, null, {
      scrollSelectedIntoView: true,
      narrowTreeToAnchorPosition: t === "responsibility" || t === "role" || t === "position",
      anchorPositionRecord: anchorPositionRecordForNarrow,
    });
    return;
  }
  await applyTreeItemSelection(itemData, null);
};

// Handle drilldown from detail components. In Pane (tree) view, keep everything in the right panel by selecting the target in the tree instead of navigating.
const handleDrilldown = async (data) => {
  if (!showOrganizationPositionsViewSelector.value) {
    emit("drilldown", data);
    return;
  }

  const route = proxy?.$route;
  const hasTreeQuery =
    route?.query?.positionId != null || route?.query?.roleId != null || route?.query?.resId != null;
  const isTreeViewUrl = Boolean(
    hasTreeQuery &&
    ((route?.name === "NewOrganization" && route?.params?.tab === "positions") ||
      route?.path?.includes("/positions"))
  );
  const shouldHandleInPanel = currentView.value === "Pane" || isTreeViewUrl;
  if (!shouldHandleInPanel) {
    emit("drilldown", data);
    return;
  }
  if (data.fromClone && data.positionId != null) {
    if (isTreeViewUrl && currentView.value !== "Pane") {
      currentView.value = "Pane";
      viewState.config.positionsViewSelection = "Pane";
      await proxy.$nextTick();
    }
    await rebuildTreeDataWithFilters();
    await proxy.$nextTick();
    await selectPanePositionInTreeAfterRoleAttached(
      data.positionId,
      data.positionName || data.name || ""
    );
    return;
  }
  if (isTreeViewUrl && currentView.value !== "Pane") {
    currentView.value = "Pane";
    viewState.config.positionsViewSelection = "Pane";
    await proxy.$nextTick();
    if (!displayedTreeData.value?.length && treeData.value?.length === 0) {
      await rebuildTreeDataWithFilters();
    }
  }
  // In-panel drilldown: update tree selection so the right panel shows the clicked entity
  // Check resId first: clicking a responsibility sends both roleId and resId; we must show ResponsibilityCard, not stay on role
  if (data.resId != null) {
    const roleId = selectedItem.value?.type === "role" ? selectedItem.value.id : data.roleId;
    const positionId = data.positionId != null ? String(data.positionId) : null;
    if (!roleId) {
      emit("drilldown", data);
      return;
    }
    const resTab =
      data.tab === "assignments"
        ? "assignments"
        : data.tab === "badge-holders"
          ? "badge-holders"
          : "checklist";
    const resIdStr = String(data.resId);
    const resName = data.resName || data.name || "";
    const route = proxy?.$route;
    const path = findPathToItem(treeData.value, roleId, "role");
    const posIdForTree = path?.[0]?.id ?? data.positionId;
    const posIdResolved =
      posIdForTree != null && posIdForTree !== "" ? String(posIdForTree) : positionId;
    const roleIdStr = String(roleId);
    const positionName = path?.[0]?.name ?? route?.query?.positionName ?? "";
    const roleName = path?.[1]?.name ?? data.roleName ?? data.name ?? route?.query?.roleName ?? "";
    // Update panel and URL immediately so ResponsibilityCard shows and shows Checklist tab
    selectedItem.value = {
      id: resIdStr,
      type: "responsibility",
      name: resName,
      ...(posIdResolved ? { parentPositionId: String(posIdResolved) } : {}),
      parentRoleId: roleIdStr,
    };
    const router = proxy?.$router;
    if (route && router) {
      const query = {
        ...route.query,
        positionId: positionId || String(route.query.positionId || ""),
        roleId: String(roleId),
        resId: resIdStr,
        resTab,
        ...(positionName ? { positionName } : {}),
        ...(roleName ? { roleName } : {}),
      };
      router.replace({ name: route.name, params: route.params, query }).catch(() => {});
    }
    // Flush Vue update so right panel switches to ResponsibilityCard before async tree expand
    await proxy.$nextTick();
    // Then expand role in tree and optionally highlight the responsibility node (skip route update so resId/resTab stay)
    await handleTreeToggleExpand({
      itemId: roleId,
      itemType: "role",
      isExpanded: true,
      skipRouteUpdate: true,
      parentId: posIdForTree != null ? String(posIdForTree) : null,
    });
    await proxy.$nextTick();
    if (posIdForTree != null && positionsSearchNavigateRef.value?.expandNodes) {
      positionsSearchNavigateRef.value.expandNodes([
        String(posIdForTree),
        `${posIdForTree}-${roleId}`,
      ]);
      await proxy.$nextTick();
    }
    if (posIdForTree != null && posIdForTree !== "" && roleIdStr) {
      ensureResponsibilityNodeUnderRole(
        String(posIdForTree),
        String(roleIdStr),
        { name: resName, id: resIdStr },
        resIdStr
      );
      await proxy.$nextTick();
    }
    const resItem = findInTree(treeData.value, resIdStr, "responsibility");
    if (resItem) {
      handleTreeItemSelected({
        id: resItem.id,
        type: resItem.type,
        name: resItem.name,
        data: resItem,
        ...(posIdResolved ? { parentPositionId: String(posIdResolved) } : {}),
        parentRoleId: roleIdStr,
      });
      updateRouteForTreeItem(resItem.id, "responsibility");
    }
    // Ensure URL always has resId and resTab (checklist) so ResponsibilityCard shows and opens Checklist tab
    if (route && router) {
      const finalQuery = {
        ...route.query,
        positionId: positionId || route.query.positionId || "",
        roleId: String(roleId),
        resId: resIdStr,
        resTab,
        ...(positionName ? { positionName } : {}),
        ...(roleName ? { roleName } : {}),
      };
      router.replace({ name: route.name, params: route.params, query: finalQuery }).catch(() => {});
    }
    return;
  }
  if (data.roleId != null) {
    const positionId =
      selectedItem.value?.type === "position" ? selectedItem.value.id : data.positionId;
    if (!positionId) {
      emit("drilldown", data);
      return;
    }
    await handleTreeToggleExpand({ itemId: positionId, itemType: "position", isExpanded: true });
    await proxy.$nextTick();
    if (positionsSearchNavigateRef.value?.expandNodes) {
      positionsSearchNavigateRef.value.expandNodes([String(positionId)]);
      await proxy.$nextTick();
    }
    const roleItem = findInTree(treeData.value, String(data.roleId), "role");
    if (roleItem) {
      handleTreeItemSelected({
        id: roleItem.id,
        type: roleItem.type,
        name: roleItem.name,
        data: roleItem,
      });
      updateRouteForTreeItem(roleItem.id, "role");
    } else {
      selectedItem.value = {
        id: String(data.roleId),
        type: "role",
        name: data.roleName || data.name || "",
      };
      updateRouteForTreeItem(data.roleId, "role");
    }
    return;
  }
  if (data.positionId != null && data.roleId == null && data.resId == null) {
    const posItem = findInTree(treeData.value, String(data.positionId), "position");
    if (posItem) {
      handleTreeItemSelected({
        id: posItem.id,
        type: posItem.type,
        name: posItem.name,
        data: posItem,
      });
      updateRouteForTreeItem(posItem.id, "position");
    } else {
      selectedItem.value = {
        id: String(data.positionId),
        type: "position",
        name: data.positionName || data.name || "",
      };
      updateRouteForTreeItem(data.positionId, "position");
    }
    return;
  }
  emit("drilldown", data);
};

const handlePositionDrillDown = (positionData) => {
  // If in Pane view, select the position in the tree
  if (currentView.value === "Pane") {
    const posId = positionData.id || positionData.position?.data?.id;
    handleTreeItemSelected({
      id: posId,
      type: "position",
      name: positionData.name || positionData.position?.name,
      data: positionData,
    });
  } else {
    // If in Grid view, navigate to detail page
    const posId = positionData.id || positionData.position?.data?.id;
    if (proxy?.$router && posId) {
      proxy.$router
        .push({
          name: "PositionDetailChild",
          params: { positionId: posId },
          query: { positionTab: "attached-roles" },
        })
        .catch(() => {});
    }
  }
};

onMounted(async () => {
  try {
    ideaMdp440Enabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_440, false)) === true;
  } catch {
    ideaMdp440Enabled.value = false;
  }

  try {
    ideaMdp551Enabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_551, false)) === true;
  } catch {
    ideaMdp551Enabled.value = false;
  }

  proxy.$loader.show();

  const route = proxy?.$route;
  const router = proxy?.$router;
  const fromDashboard = route?.query?.fromDashboard === "true";
  const searchPositionName = route?.query?.searchPosition || "";

  if (positions.getDynamicPositionFilters()?.name) {
    positions.removePositionFilter("name");
  }
  positions.setPaginationDataOffset(0);
  // Restore persisted filter state (grid + pane departments) before loading.
  viewState.getFilters();

  redirectOrganizationPositionsToListViewIfNeeded(router, route);

  if (fromDashboard && searchPositionName && searchPositionName.trim() !== "") {
    await proxy.$nextTick();
    await new Promise((resolve) => setTimeout(resolve, 300));

    const decodedName = decodeURIComponent(searchPositionName.trim());

    viewState.state.filters.searchText = decodedName;

    await proxy.$nextTick();

    positions.removePositionFilter("name");
    if (decodedName && decodedName.trim() !== "") {
      positions.addPositionFilter("name", decodedName);
    }
    positions.setPaginationDataOffset(0);
    await positions.loadAllPositions();

    await proxy.$nextTick();

    const allPositions = positions.allPositions.value || [];
    const foundPosition = allPositions.find((pos) => pos.name === decodedName);

    if (foundPosition) {
      setTimeout(() => {
        highlight({ id: foundPosition.id, type: "Positions" });
      }, 500);
    }

    if (router && route) {
      router.replace({
        path: route.path,
        query: {},
      });
    }
  } else {
    await positions.loadAllPositions();
  }
  hasInitialPositionsLoad.value = true;

  // Initialize view selection from viewState
  initializeView();

  // Initialize pane filters from viewState if available; ensure default Active filter for tree view
  if (currentView.value === "Pane") {
    paneSearchText.value =
      proxy?.$store?.getters?.["featureFlags/dragDropAttachOrgObjectsPositionTreeMDP434"] === true
        ? viewState.state.filters.searchText || ""
        : "";
    paneAppliedFilters.value = [...(viewState.state.filters.values || [])];
    ensurePaneDefaultFilters();
    await proxy.$nextTick();
    attachPaneViewRightHeightObserver();
  }

  proxy.$loader.hide();
});

onBeforeUnmount(() => {
  removeLeftPanelWheelListener();
  detachPaneViewRightHeightObserver();
});
</script>
<style>
.org-pos-tab {
  box-shadow:
    0px 1px 3px 0px #1018281a,
    0px 1px 2px 0px #1018280f;
  border-radius: 8px;
  margin-top: 15px;
}
.organization-positions-tab-wrapper {
  width: 100%;
}

.pane-view-container {
  width: 100%;
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.pane-view-layout {
  display: flex;
  gap: 0;
  flex: 1;
  min-height: 0;
  width: 100%;
}

.pane-view-left {
  flex: 0 0 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  overflow-y: none !important;
  border: 1px solid #d1caca;
  border-radius: 16px !important;
  padding: 10px;
  scroll-behavior: smooth;
  height: 100%;
  position: sticky;
  top: 0;
}

.pane-view-resize-handle {
  flex: 0 0 8px;
  width: 8px;
  min-height: 0;
  cursor: col-resize;
  background: transparent;
  align-self: stretch;
  position: relative;
}
.pane-view-resize-handle:hover,
.pane-view-resize-handle:active {
  background: rgba(0, 0, 0, 0.06);
}
.pane-view-resize-handle::after {
  content: "";
  position: absolute;
  left: 3px;
  top: 50%;
  transform: translateY(-50%);
  width: 2px;
  height: 40px;
  border-radius: 1px;
  background: #d1caca;
  opacity: 0.6;
}
.pane-view-resize-handle:hover::after {
  opacity: 1;
  background: #9ca3af;
}

.pane-view-left:focus-visible {
  outline: none !important;
}

.pane-view-left-scroll:focus-visible {
  outline: none !important;
}

.pane-view-left-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box; /* keep scrollbar inside so it doesn’t get clipped */
}

.pane-view-left-scroll.pane-view-left-scroll--has-content {
  overflow-y: scroll;
}

.pane-view-left-scroll:focus-visible {
  outline: none !important;
}

/* No scrollbar at all when no positions (inner min-height would still cause overflow with auto) */
.pane-view-left-scroll.pane-view-left-scroll--empty {
  overflow-y: hidden;
}

.pane-view-left-scroll::-webkit-scrollbar {
  width: 10px;
  height: 0; /* no horizontal scrollbar - prevents gray bar at bottom */
}

.pane-view-left-scroll::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 4px;
}

.pane-view-left-scroll::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.pane-view-left-scroll::-webkit-scrollbar-thumb:hover {
  background: #a0a0a0;
}

/* Remove the corner square where scrollbars meet (was showing as a gray bar at bottom) */
.pane-view-left-scroll::-webkit-scrollbar-corner {
  background: transparent;
  display: none;
}

.pane-view-right {
  flex: 1;
  padding: 0;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  min-width: 0;
}

/* Wide right-pane tables scroll inside the pane; the page itself never scrolls horizontally. */
.pane-view-right .dx-datagrid,
.pane-view-right .dx-treelist {
  max-width: 100%;
  min-width: 0;
}

/* Sizing the scrollbar makes Chrome draw a classic bar, so it stays visible with overlay scrollbars (macOS). */
.pane-view-right .dx-datagrid-rowsview .dx-scrollable-container::-webkit-scrollbar,
.pane-view-right .dx-treelist-rowsview .dx-scrollable-container::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

.pane-view-right .dx-datagrid-rowsview .dx-scrollable-container::-webkit-scrollbar-track,
.pane-view-right .dx-treelist-rowsview .dx-scrollable-container::-webkit-scrollbar-track {
  background: #f2f4f7;
  border-radius: 4px;
}

.pane-view-right .dx-datagrid-rowsview .dx-scrollable-container::-webkit-scrollbar-thumb,
.pane-view-right .dx-treelist-rowsview .dx-scrollable-container::-webkit-scrollbar-thumb {
  background: #667085;
  border-radius: 4px;
}

.pane-view-right .dx-datagrid-rowsview .dx-scrollable-container::-webkit-scrollbar-thumb:hover,
.pane-view-right .dx-treelist-rowsview .dx-scrollable-container::-webkit-scrollbar-thumb:hover {
  background: #475467;
}

/* Full-width row: filter chips + More Filters + grid/pane view toggle (search field is only above the tree) */
.pane-view-filters-bar {
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow:
    0px 1px 2px 0px #1018280f,
    0px 1px 3px 0px #1018281a;
  border: 1px solid #eaecf0;
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
}

.pane-view-filters-bar-inner {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.pane-department-filter-tag {
  flex-shrink: 0;
}

.pane-search-bar-filters-only {
  flex: 1;
  min-width: 0;
}

.pane-view-selector-wrapper {
  flex-shrink: 0;
}

.pane-filters-add-btn {
  flex-shrink: 0;
}

/* Left panel: search field only, then tree */
.pane-left-tree-search-row {
  padding: 0 4px 12px;
  margin-bottom: 4px;
  border-bottom: 1px solid #eaecf0;
  flex-shrink: 0;
}

.pane-tree-search-input {
  width: 100%;
  min-width: 0;
}

.pane-view-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 400px;
}

.empty-state {
  text-align: center;
  color: #6b7280;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
}

/* Full-page overlay: transparent cursor so only our custom component is visible (pointer-events: none so drop works) */
.pane-checkpoint-drag-cursor-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  cursor:
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'/%3E") 0 0,
    none !important;
  pointer-events: none;
  background: transparent;
}

/* Custom cursor component: above overlay and drag ghost so hand icon stays visible */
.pane-checkpoint-drag-cursor {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 2147483647;
  pointer-events: none;
  display: block;
  transform: translateZ(0);
}

/* Fallback: transparent cursor on html/body and all descendants during checkpoint drag */
html.checkpoint-drag-cursor-active,
html.checkpoint-drag-cursor-active *,
body.checkpoint-drag-cursor-active,
body.checkpoint-drag-cursor-active * {
  cursor:
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'/%3E") 0 0,
    none !important;
}
</style>
